/**
 * Central API Service
 * Manages all API calls to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    // Request deduplication: Track in-flight requests to prevent duplicates
    this.pendingRequests = new Map();
  }

  /**
   * Get authentication token from localStorage
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Get user ID from localStorage
   */
  getUserId() {
    return localStorage.getItem('userId');
  }

  /**
   * Get default headers for API requests
   */
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = this.getToken();
      const userId = this.getUserId();
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (userId) {
        headers['x-user-id'] = userId;
      }
    }

    return headers;
  }

  /**
   * Generate a unique key for request deduplication
   */
  getRequestKey(endpoint, options = {}) {
    const method = options.method || 'GET';
    const body = options.body ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body)) : '';
    // Include method, endpoint, and body in the key for accurate deduplication
    return `${method}:${endpoint}:${body}`;
  }

  /**
   * Make API request with automatic deduplication
   */
  async request(endpoint, options = {}) {
    // Generate unique key for this request
    const requestKey = this.getRequestKey(endpoint, options);
    
    // Check if same request is already in-flight
    if (this.pendingRequests.has(requestKey)) {
      console.log(`[API] Deduplicating request: ${requestKey}`);
      return this.pendingRequests.get(requestKey);
    }
    
    // Create the actual request promise
    const requestPromise = this._executeRequest(endpoint, options);
    
    // Store the promise to deduplicate future identical requests
    this.pendingRequests.set(requestKey, requestPromise);
    
    // Clean up after request completes (success or failure)
    requestPromise
      .then(() => {
        this.pendingRequests.delete(requestKey);
      })
      .catch(() => {
        this.pendingRequests.delete(requestKey);
      });
    
    return requestPromise;
  }

  /**
   * Execute the actual HTTP request (internal method)
   */
  async _executeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Handle FormData - don't set Content-Type header, let browser set it with boundary
    const isFormData = options.body instanceof FormData;
    let defaultHeaders = {};
    
    if (!isFormData) {
      // For non-FormData, use normal headers with Content-Type
      defaultHeaders = this.getHeaders(options.includeAuth !== false);
    } else {
      // For FormData, only include auth headers (no Content-Type)
      if (options.includeAuth !== false) {
        const token = this.getToken();
        const userId = this.getUserId();
        if (token) {
          defaultHeaders['Authorization'] = `Bearer ${token}`;
        }
        if (userId) {
          defaultHeaders['x-user-id'] = userId;
        }
      }
    }
    
    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      console.log(`[API] ${options.method || 'GET'} ${endpoint}`);
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized - redirect to login (user is not authenticated)
      // Exception: Don't auto-logout for password change endpoint - wrong password is a validation error, not auth error
      if (response.status === 401 && !endpoint.includes('/api/v1/users/me/password')) {
        // Clear authentication data
        this.logout();
        // Redirect to login page
        window.location.href = '/login';
        // Create error object
        const error = new Error('Unauthorized. Please login again.');
        error.status = response.status;
        throw error;
      }
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      
      const data = isJson ? await response.json() : await response.text();

      if (!response.ok) {
        // Create error object with more details
        // For 403, use the message from the API response
        const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      // If it's already a 403 error we handled, just rethrow it
      if (error.status === 403) {
        throw error;
      }
      throw error;
    }
  }

  /**
   * Upload request with progress callback support
   * Uses XMLHttpRequest because fetch does not provide upload progress events.
   */
  async uploadWithProgress(endpoint, formData, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const method = options.method || 'POST';
    const onUploadProgress = options.onUploadProgress;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);

      const token = this.getToken();
      const userId = this.getUserId();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      if (userId) xhr.setRequestHeader('x-user-id', userId);

      if (typeof onUploadProgress === 'function' && xhr.upload) {
        xhr.upload.onprogress = (event) => {
          const total = event.total || 0;
          const progress = total > 0 ? Math.round((event.loaded / total) * 100) : null;
          onUploadProgress({
            loaded: event.loaded,
            total,
            progress,
            lengthComputable: event.lengthComputable,
          });
        };
      }

      xhr.onerror = () => {
        reject(new Error('Network error while uploading file.'));
      };

      xhr.onload = () => {
        const contentType = xhr.getResponseHeader('content-type') || '';
        const isJson = contentType.includes('application/json');
        const raw = xhr.responseText;

        let data = raw;
        if (isJson) {
          try {
            data = raw ? JSON.parse(raw) : {};
          } catch (e) {
            data = raw;
          }
        }

        if (xhr.status === 401 && !endpoint.includes('/api/v1/users/me/password')) {
          this.logout();
          window.location.href = '/login';
          const error = new Error('Unauthorized. Please login again.');
          error.status = xhr.status;
          reject(error);
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${xhr.status}`;
          const error = new Error(errorMessage);
          error.status = xhr.status;
          error.data = data;
          reject(error);
          return;
        }

        resolve(data);
      };

      xhr.send(formData);
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // ==================== Authentication APIs ====================

  /**
   * Persist login payload in localStorage
   */
  setAuthData(response) {
    if (!response || !response.token) return;

    localStorage.setItem('token', response.token);
    localStorage.setItem('userId', response.userId);
    localStorage.setItem('userEmail', response.email);
    localStorage.setItem('userFirstName', response.firstName);
    localStorage.setItem('userLastName', response.lastName);
    localStorage.setItem('roleId', response.roleId);
    localStorage.setItem('roleMeta', JSON.stringify(response.roleMeta || {}));
    localStorage.setItem('perms', JSON.stringify(response.perms || []));
    localStorage.setItem('expiresAt', response.expiresAt);
  }

  /**
   * Login user
   * POST /api/v1/auth/login
   */
  async login(email, password) {
    const response = await this.post(
      '/api/v1/auth/login',
      { email, password },
      { includeAuth: false }
    );
    
    this.setAuthData(response);
    
    return response;
  }

  /**
   * Start Microsoft SSO login by redirecting browser to backend endpoint
   * GET /api/v1/auth/microsoft/start
   */
  startMicrosoftLogin() {
    window.location.href = `${this.baseURL}/api/v1/auth/microsoft/start`;
  }

  /**
   * Exchange one-time Microsoft code for standard login payload
   * POST /api/v1/auth/microsoft/exchange
   */
  async exchangeMicrosoftCode(code) {
    const response = await this.post(
      '/api/v1/auth/microsoft/exchange',
      { code },
      { includeAuth: false }
    );

    this.setAuthData(response);
    return response;
  }

  /**
   * Logout user
   * POST /api/v1/auth/logout
   */
  async logout() {
    const userId = this.getUserId();
    
    if (userId) {
      try {
        await this.post('/api/v1/auth/logout', {}, {
          headers: {
            'x-user-id': userId,
          },
        });
      } catch (error) {
        console.error('Logout API error:', error);
        // Continue with logout even if API call fails
      }
    }
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('roleId');
    localStorage.removeItem('roleMeta');
    localStorage.removeItem('perms');
    localStorage.removeItem('expiresAt');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const token = this.getToken();
    const expiresAt = localStorage.getItem('expiresAt');
    
    if (!token) return false;
    
    // Check if token is expired
    if (expiresAt) {
      const expirationTime = parseInt(expiresAt, 10);
      if (Date.now() >= expirationTime) {
        this.logout();
        return false;
      }
    }
    
    return true;
  }

  /**
   * Get current user info from localStorage
   */
  getCurrentUser() {
    if (!this.isAuthenticated()) {
      return null;
    }

    return {
      userId: this.getUserId(),
      email: localStorage.getItem('userEmail'),
      firstName: localStorage.getItem('userFirstName'),
      lastName: localStorage.getItem('userLastName'),
      roleId: localStorage.getItem('roleId'),
      roleMeta: JSON.parse(localStorage.getItem('roleMeta') || '{}'),
      perms: JSON.parse(localStorage.getItem('perms') || '[]'),
    };
  }

  // ==================== User Management APIs ====================

  /**
   * Create a new user
   * POST /api/v1/users
   */
  async createUser(userData) {
    return this.post('/api/v1/users', userData);
  }

  /**
   * Get all users
   * GET /api/v1/users
   */
  async getAllUsers() {
    return this.get('/api/v1/users');
  }

  /**
   * Get all users for caching (no permission required, authentication only)
   * GET /api/v1/users/cache
   * Used by UI for audit screens and user caching
   */
  async getAllUsersCache() {
    return this.get('/api/v1/users/cache');
  }

  /**
   * Get user by ID
   * GET /api/v1/users/{userId}
   * Requires x-user-id header (automatically included via getHeaders)
   */
  async getUserById(userId) {
    // x-user-id header is automatically included via getHeaders() in the request method
    return this.get(`/api/v1/users/${userId}`);
  }

  /**
   * Update a user
   * PATCH /api/v1/users/{userId}
   */
  async updateUser(userId, userData) {
    return this.patch(`/api/v1/users/${userId}`, userData);
  }

  /**
   * Update user status
   * PATCH /api/v1/users/{userId}/status
   */
  async updateUserStatus(userId, status) {
    // Ensure x-user-id header is included (logged-in admin's ID)
    const adminUserId = this.getUserId();
    return this.patch(`/api/v1/users/${userId}/status`, { status }, {
      headers: {
        'x-user-id': adminUserId,
      },
    });
  }

  /**
   * Change user password
   * PATCH /api/v1/users/me/password
   * Requires x-user-id header (automatically included via getHeaders)
   */
  async changePassword(currentPassword, newPassword) {
    // x-user-id header is automatically included via getHeaders() in the request method
    return this.patch('/api/v1/users/me/password', {
      currentPassword,
      newPassword
    });
  }

  /**
   * Reset user password (ADMIN only)
   * POST /api/v1/users/{userId}/password/reset
   * ADMIN can reset a user's password. If newPassword is provided, it will be set.
   * Requires x-user-id header (automatically included via getHeaders)
   */
  async resetUserPassword(userId, newPassword) {
    // x-user-id header is automatically included via getHeaders() in the request method
    return this.post(`/api/v1/users/${userId}/password/reset`, {
      newPassword
    });
  }

  // ==================== Role Management APIs ====================

  /**
   * Get all roles
   * GET /api/v1/roles
   */
  async getAllRoles(roleName = null) {
    const params = roleName ? `?roleName=${encodeURIComponent(roleName)}` : '';
    return this.get(`/api/v1/roles${params}`);
  }

  /**
   * Create a new role
   * POST /api/v1/roles
   */
  async createRole(roleData) {
    return this.post('/api/v1/roles', roleData);
  }

  /**
   * Update a role
   * PUT /api/v1/roles/{roleId}
   * Includes x-user-id header automatically (authenticated user's UUID)
   */
  async updateRole(roleId, roleData) {
    // x-user-id header is automatically included via getHeaders()
    return this.put(`/api/v1/roles/${roleId}`, roleData);
  }

  /**
   * Update role status
   * PATCH /api/v1/roles/{roleId}/status
   */
  async updateRoleStatus(roleId, status) {
    return this.patch(`/api/v1/roles/${roleId}/status`, { status });
  }

  /**
   * Update role permissions
   * PATCH /api/v1/roles/{roleId}/permissions
   */
  async updateRolePermissions(roleId, permissions) {
    return this.patch(`/api/v1/roles/${roleId}/permissions`, { permissions });
  }

  // ==================== Permissions APIs ====================

  /**
   * Get all available permissions
   * GET /api/v1/permissions
   */
  async getAllPermissions() {
    return this.get('/api/v1/permissions');
  }

  // ==================== Public APIs ====================

  /**
   * Get all public roles (roleId and roleName only)
   * GET /api/v1/public/roles
   * Requires x-user-id header (automatically included via getHeaders)
   */
  async getPublicRoles(roleName = null) {
    const params = roleName ? `?roleName=${encodeURIComponent(roleName)}` : '';
    // x-user-id header is automatically included via getHeaders() in the request method
    return this.get(`/api/v1/public/roles${params}`, {
      includeAuth: true // Ensure auth headers including x-user-id are included
    });
  }

  // ==================== Practice Management APIs ====================

  /**
   * Get all practices
   * GET /api/v1/practices
   */
  async getAllPractices() {
    return this.get('/api/v1/practices');
  }

  /**
   * Create a new practice
   * POST /api/v1/practices
   */
  async createPractice(practiceData) {
    return this.post('/api/v1/practices', practiceData);
  }

  /**
   * Update a practice
   * PATCH /api/v1/practices/{practiceId}
   */
  async updatePractice(practiceId, practiceData) {
    return this.patch(`/api/v1/practices/${practiceId}`, practiceData);
  }

  /**
   * Update practice status
   * PATCH /api/v1/practices/{practiceId}/status
   */
  async updatePracticeStatus(practiceId, status) {
    return this.patch(`/api/v1/practices/${practiceId}/status`, { status });
  }

  /**
   * Check if practice code exists
   * GET /api/v1/practices/code-exists?code={code}
   */
  async checkPracticeCodeExists(code) {
    return this.get(`/api/v1/practices/code-exists?code=${encodeURIComponent(code)}`);
  }

  /**
   * Get all locations for a practice
   * GET /api/v1/practices/{practiceId}/locations
   */
  async getPracticeLocations(practiceId) {
    return this.get(`/api/v1/practices/${practiceId}/locations`);
  }

  /**
   * Create a new location for a practice
   * POST /api/v1/practices/{practiceId}/locations
   */
  async createPracticeLocation(practiceId, locationData) {
    return this.post(`/api/v1/practices/${practiceId}/locations`, locationData);
  }

  /**
   * Update a location
   * PATCH /api/v1/practices/{practiceId}/locations/{locationId}
   */
  async updatePracticeLocation(practiceId, locationId, locationData) {
    return this.patch(`/api/v1/practices/${practiceId}/locations/${locationId}`, locationData);
  }

  /**
   * Update location status
   * PATCH /api/v1/practices/{practiceId}/locations/{locationId}/status
   */
  async updateLocationStatus(practiceId, locationId, isActive) {
    return this.patch(`/api/v1/practices/${practiceId}/locations/${locationId}/status`, { isActive });
  }

  /**
   * Check if location code exists
   * GET /api/v1/practices/locations/code-exists?code={code}
   */
  async checkLocationCodeExists(code) {
    return this.get(`/api/v1/practices/locations/code-exists?code=${encodeURIComponent(code)}`);
  }

  // ==================== Master Data APIs ====================

  /**
   * Get all master data (no metadataType filter)
   * GET /api/v1/master-data
   */
  /**
   * Get master data with optional filters
   * GET /api/v1/master-data?metadataType={type}&active={true/false}
   * @param {Object} params - Query parameters
   * @param {string} params.metadataType - Filter by metadata type (e.g., 'CHECK_TYPE', 'CLARIFICATION_TYPE')
   * @param {boolean} params.active - Filter by active flag
   */
  async getMasterData(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.metadataType) {
      queryParams.append('metadataType', params.metadataType);
    }
    
    if (params.active !== undefined && params.active !== null) {
      queryParams.append('active', params.active);
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/master-data?${queryString}` : '/api/v1/master-data';
    
    return this.get(endpoint);
  }

  /**
   * List supported master-data type enum names (MetadataType)
   * GET /api/v1/master-data/types
   * @returns {Promise<string[]>}
   */
  async getMasterDataTypes() {
    return this.get('/api/v1/master-data/types');
  }

  /**
   * Create master data
   * POST /api/v1/master-data
   */
  async createMasterData(data) {
    return this.post('/api/v1/master-data', data);
  }

  /**
   * Update master data (name, description, sortOrder only)
   * PUT /api/v1/master-data/{uuid}
   */
  async updateMasterData(uuid, data) {
    return this.put(`/api/v1/master-data/${uuid}`, data);
  }

  /**
   * Update master data active status
   * PATCH /api/v1/master-data/{uuid}/active
   */
  async updateMasterDataActive(uuid, isActive) {
    return this.patch(`/api/v1/master-data/${uuid}/active`, { isActive });
  }

  /**
   * Delete master data (only when isActive=false)
   * DELETE /api/v1/master-data/{uuid}
   */
  async deleteMasterData(uuid) {
    return this.delete(`/api/v1/master-data/${uuid}`);
  }

  // ==================== Check Management APIs ====================

  /**
   * Get checks dashboard with filters
   * GET /api/v1/checks/dashboard
   * @param {Object} params - Query parameters
   * @param {string} params.status - Check status
   * @param {string} params.practiceCode - Practice code
   * @param {string} params.locationCode - Location code
   * @param {string} params.assigneeId - Assignee user ID (UUID)
   * @param {string} params.reporterId - Reporter user ID (UUID)
   * @param {string} params.checkNumber - Check number
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {number} params.month - Month (1-12)
   * @param {number} params.year - Year
   * @param {number} params.page - Page number (default: 0)
   * @param {number} params.size - Page size (default: 50)
   */
  async getChecksDashboard(params = {}) {
    const queryParams = new URLSearchParams();
    
    // Add all non-empty parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/checks/dashboard${queryString ? `?${queryString}` : ''}`;
    
    return this.get(endpoint);
  }

  /**
   * Search checks dashboard with filters (POST)
   * POST /api/v1/checks/dashboard/search?unknown={true|false}
   * @param {Object} searchParams - Search parameters in request body
   * @param {boolean} searchParams.unknown - Whether to search for unknown checks (default: false)
   * @param {Array<string>} searchParams.statuses - Check statuses array
   * @param {Array<string>} searchParams.practiceCodes - Practice codes array
   * @param {Array<string>} searchParams.locationCodes - Location codes array
   * @param {Array<string>} searchParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} searchParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {Array<string>} searchParams.checkTypes - Check types array (EFT, ERA, DIT_DRL, etc.)
   * @param {Array<string>} searchParams.workflowStatuses - Workflow status codes array
   * @param {string} searchParams.checkNumber - Check number (supports wildcards like "*CHE")
   * @param {string} searchParams.depositDateFrom - Deposit date from (YYYY-MM-DD)
   * @param {string} searchParams.depositDateTo - Deposit date to (YYYY-MM-DD)
   * @param {string} searchParams.receivedDateFrom - Received date from (YYYY-MM-DD)
   * @param {string} searchParams.receivedDateTo - Received date to (YYYY-MM-DD)
   * @param {string} searchParams.completedDateFrom - Completed date from (YYYY-MM-DD)
   * @param {string} searchParams.completedDateTo - Completed date to (YYYY-MM-DD)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 50)
   */
  async searchChecksDashboard(searchParams = {}) {
    // Only include unknown parameter in query string if explicitly provided
    let endpoint = '/api/v1/checks/dashboard/search';
    if (searchParams.unknown !== undefined && searchParams.unknown !== null) {
      endpoint = `/api/v1/checks/dashboard/search?unknown=${searchParams.unknown}`;
    }
    
    // Build request body, only including non-empty values
    const body = {};
    
    // Arrays - only include if not empty
    if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
      body.statuses = searchParams.statuses;
    }
    if (searchParams.excludeStatuses && Array.isArray(searchParams.excludeStatuses) && searchParams.excludeStatuses.length > 0) {
      body.excludeStatuses = searchParams.excludeStatuses;
    }
    if (searchParams.practiceCodes && Array.isArray(searchParams.practiceCodes) && searchParams.practiceCodes.length > 0) {
      body.practiceCodes = searchParams.practiceCodes;
    }
    if (searchParams.excludePracticeCodes && Array.isArray(searchParams.excludePracticeCodes) && searchParams.excludePracticeCodes.length > 0) {
      body.excludePracticeCodes = searchParams.excludePracticeCodes;
    }
    if (searchParams.locationCodes && Array.isArray(searchParams.locationCodes) && searchParams.locationCodes.length > 0) {
      body.locationCodes = searchParams.locationCodes;
    }
    if (searchParams.excludeLocationCodes && Array.isArray(searchParams.excludeLocationCodes) && searchParams.excludeLocationCodes.length > 0) {
      body.excludeLocationCodes = searchParams.excludeLocationCodes;
    }
    if (searchParams.assigneeIds && Array.isArray(searchParams.assigneeIds) && searchParams.assigneeIds.length > 0) {
      body.assigneeIds = searchParams.assigneeIds;
    }
    if (searchParams.excludeAssigneeIds && Array.isArray(searchParams.excludeAssigneeIds) && searchParams.excludeAssigneeIds.length > 0) {
      body.excludeAssigneeIds = searchParams.excludeAssigneeIds;
    }
    if (searchParams.reporterIds && Array.isArray(searchParams.reporterIds) && searchParams.reporterIds.length > 0) {
      body.reporterIds = searchParams.reporterIds;
    }
    if (searchParams.excludeReporterIds && Array.isArray(searchParams.excludeReporterIds) && searchParams.excludeReporterIds.length > 0) {
      body.excludeReporterIds = searchParams.excludeReporterIds;
    }
    if (searchParams.checkTypes && Array.isArray(searchParams.checkTypes) && searchParams.checkTypes.length > 0) {
      body.checkTypes = searchParams.checkTypes;
    }
    if (searchParams.excludeCheckTypes && Array.isArray(searchParams.excludeCheckTypes) && searchParams.excludeCheckTypes.length > 0) {
      body.excludeCheckTypes = searchParams.excludeCheckTypes;
    }
    if (searchParams.workflowStatuses && Array.isArray(searchParams.workflowStatuses) && searchParams.workflowStatuses.length > 0) {
      body.workflowStatuses = searchParams.workflowStatuses;
    }
    if (searchParams.excludeWorkflowStatuses && Array.isArray(searchParams.excludeWorkflowStatuses) && searchParams.excludeWorkflowStatuses.length > 0) {
      body.excludeWorkflowStatuses = searchParams.excludeWorkflowStatuses;
    }
    
    // Strings - only include if not empty
    if (searchParams.checkNumber && searchParams.checkNumber.trim() !== '') {
      body.checkNumber = searchParams.checkNumber.trim();
    }
    if (searchParams.batchNumber && searchParams.batchNumber.trim() !== '') {
      body.batchNumber = searchParams.batchNumber.trim();
    }
    if (searchParams.payer && searchParams.payer.trim() !== '') {
      body.payer = searchParams.payer.trim();
    }
    
    // Dates - only include if not empty
    if (searchParams.depositDateFrom && searchParams.depositDateFrom.trim() !== '') {
      body.depositDateFrom = searchParams.depositDateFrom.trim();
    }
    if (searchParams.depositDateTo && searchParams.depositDateTo.trim() !== '') {
      body.depositDateTo = searchParams.depositDateTo.trim();
    }
    if (searchParams.receivedDateFrom && searchParams.receivedDateFrom.trim() !== '') {
      body.receivedDateFrom = searchParams.receivedDateFrom.trim();
    }
    if (searchParams.receivedDateTo && searchParams.receivedDateTo.trim() !== '') {
      body.receivedDateTo = searchParams.receivedDateTo.trim();
    }
    if (searchParams.completedDateFrom && searchParams.completedDateFrom.trim() !== '') {
      body.completedDateFrom = searchParams.completedDateFrom.trim();
    }
    if (searchParams.completedDateTo && searchParams.completedDateTo.trim() !== '') {
      body.completedDateTo = searchParams.completedDateTo.trim();
    }

    // Sort - only include if sortBy is provided
    if (searchParams.sortBy && searchParams.sortBy.trim() !== '') {
      body.sortBy = searchParams.sortBy.trim();
      body.sortDirection = (searchParams.sortDirection && searchParams.sortDirection.trim() !== '')
        ? searchParams.sortDirection.trim()
        : 'ASC';
    }
    
    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 50;
    
    return this.post(endpoint, body);
  }

  /**
   * Create a new check
   * POST /api/v1/checks
   * @param {Object} checkData - Check data
   */
  async createCheck(checkData) {
    return this.post('/api/v1/checks', checkData);
  }

  /**
   * Bulk update checks (assignee/reporter/unknown and optionally create clarifications)
   * POST /api/v1/checks/bulk-actions
   * @param {Object} payload - Bulk action payload
   * @param {Array<string>} payload.checkIds - Array of check IDs (UUIDs)
   * @param {string} [payload.assigneeId] - Assignee user ID (UUID)
   * @param {string} [payload.reporterId] - Reporter user ID (UUID)
   * @param {boolean} [payload.unknown] - Mark checks as unknown
   * @param {Object} [payload.clarification] - Optional clarification object
   * @param {string} [payload.clarification.clarificationType] - Type of clarification
   * @param {string} [payload.clarification.details] - Clarification details
   * @param {string} [payload.clarification.status] - Clarification status (OPEN/RESOLVED)
   * @param {string} [payload.clarification.assigneeId] - Clarification assignee ID
   * @param {string} [payload.clarification.reporterId] - Clarification reporter ID
   * @param {string} [payload.clarification.assignee] - Assignee type (ON-SHORE/OFF-SHORE)
   * @param {string} [payload.clarification.reportee] - Reportee type (EBOTICS)
   */
  async bulkUpdateChecks(payload) {
    return this.post('/api/v1/checks/bulk-actions', payload);
  }

  /**
   * Get check by ID
   * GET /api/v1/checks/{checkId}
   * @param {string} checkId - Check ID (UUID)
   */
  async getCheckById(checkId) {
    return this.get(`/api/v1/checks/${checkId}`);
  }

  /**
   * Update check
   * PATCH /api/v1/checks/{checkId}
   * @param {string} checkId - Check ID (UUID)
   * @param {Object} checkData - Check data to update
   */
  async updateCheck(checkId, checkData) {
    return this.patch(`/api/v1/checks/${checkId}`, checkData);
  }

  /**
   * Move check to unknown
   * POST /api/v1/checks/{checkId}/move-to-unknown
   * @param {string} checkId - Check ID (UUID)
   * @param {string} [unknownAccountNumber] - Optional unknown account number
   */
  async moveCheckToUnknown(checkId, unknownAccountNumber) {
    const payload = {};
    if (unknownAccountNumber !== undefined && unknownAccountNumber !== null && String(unknownAccountNumber).trim() !== '') {
      payload.unknownAccountNumber = String(unknownAccountNumber).trim();
    }
    return this.post(`/api/v1/checks/${checkId}/move-to-unknown`, payload);
  }

  /**
   * Delete check
   * DELETE /api/v1/checks/{checkId}
   * @param {string} checkId - Check ID (UUID)
   */
  async deleteCheck(checkId) {
    return this.delete(`/api/v1/checks/${checkId}`);
  }

  // ==================== Trash / Recycle-Bin APIs ====================

  /**
   * Search trashed checks dashboard with filters
   * POST /api/v1/trash/checks/dashboard/search
   * @param {Object} searchParams - Search parameters (same shape as searchChecksDashboard)
   */
  async searchTrashChecksDashboard(searchParams = {}) {
    const body = {};

    if (searchParams.statuses?.length) body.statuses = searchParams.statuses;
    if (searchParams.practiceCodes?.length) body.practiceCodes = searchParams.practiceCodes;
    if (searchParams.locationCodes?.length) body.locationCodes = searchParams.locationCodes;
    if (searchParams.assigneeIds?.length) body.assigneeIds = searchParams.assigneeIds;
    if (searchParams.excludeAssigneeIds?.length) body.excludeAssigneeIds = searchParams.excludeAssigneeIds;
    if (searchParams.reporterIds?.length) body.reporterIds = searchParams.reporterIds;
    if (searchParams.excludeReporterIds?.length) body.excludeReporterIds = searchParams.excludeReporterIds;
    if (searchParams.checkTypes?.length) body.checkTypes = searchParams.checkTypes;
    if (searchParams.workflowStatuses?.length) body.workflowStatuses = searchParams.workflowStatuses;
    if (searchParams.checkNumber?.trim()) body.checkNumber = searchParams.checkNumber.trim();
    if (searchParams.batchNumber?.trim()) body.batchNumber = searchParams.batchNumber.trim();
    if (searchParams.payer?.trim()) body.payer = searchParams.payer.trim();
    if (searchParams.depositDateFrom?.trim()) body.depositDateFrom = searchParams.depositDateFrom.trim();
    if (searchParams.depositDateTo?.trim()) body.depositDateTo = searchParams.depositDateTo.trim();
    if (searchParams.receivedDateFrom?.trim()) body.receivedDateFrom = searchParams.receivedDateFrom.trim();
    if (searchParams.receivedDateTo?.trim()) body.receivedDateTo = searchParams.receivedDateTo.trim();
    if (searchParams.completedDateFrom?.trim()) body.completedDateFrom = searchParams.completedDateFrom.trim();
    if (searchParams.completedDateTo?.trim()) body.completedDateTo = searchParams.completedDateTo.trim();
    if (searchParams.unknown !== undefined && searchParams.unknown !== null) body.unknown = searchParams.unknown;

    body.page = searchParams.page ?? 0;
    body.size = searchParams.size ?? 50;

    return this.post('/api/v1/trash/checks/dashboard/search', body);
  }

  /**
   * Get trashed check details (full details including batches, clarifications, activities)
   * GET /api/v1/trash/checks/{checkId}/details
   * @param {string} checkId - Check UUID
   */
  async getTrashCheckDetails(checkId) {
    return this.get(`/api/v1/trash/checks/${checkId}/details`);
  }

  /**
   * Restore a trashed check back to the main check list
   * POST /api/v1/trash/checks/{checkId}/restore
   * @param {string} checkId - Check UUID
   */
  async restoreTrashCheck(checkId) {
    return this.post(`/api/v1/trash/checks/${checkId}/restore`, {});
  }

  /**
   * Permanently delete a trashed check (irreversible)
   * DELETE /api/v1/trash/checks/{checkId}
   * @param {string} checkId - Check UUID
   */
  async permanentDeleteTrashCheck(checkId) {
    return this.delete(`/api/v1/trash/checks/${checkId}`);
  }

  // ==================== Batch Management APIs ====================

  /**
   * Create batch for a check
   * POST /api/v1/checks/{checkId}/batches
   * @param {string} checkId - Check ID (UUID)
   * @param {Object} batchData - Batch data
   */
  async createBatch(checkId, batchData) {
    return this.post(`/api/v1/checks/${checkId}/batches`, batchData);
  }

  /**
   * Update batch
   * PATCH /api/v1/checks/{checkId}/batches/{batchId}
   * @param {string} checkId - Check ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {Object} batchData - Batch data to update
   */
  async updateBatch(checkId, batchId, batchData) {
    return this.patch(`/api/v1/checks/${checkId}/batches/${batchId}`, batchData);
  }

  /**
   * Archive/Unarchive batch
   * POST /api/v1/checks/{checkId}/batches/{batchId}/archive?archived={true|false}
   * @param {string} checkId - Check ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {boolean} archived - Whether to archive (true) or unarchive (false)
   */
  async archiveBatch(checkId, batchId, archived = true) {
    return this.post(`/api/v1/checks/${checkId}/batches/${batchId}/archive?archived=${archived}`, {});
  }

  /**
   * Delete batch
   * DELETE /api/v1/checks/{checkId}/batches/{batchId}
   * @param {string} checkId - Check ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   */
  async deleteBatch(checkId, batchId) {
    return this.delete(`/api/v1/checks/${checkId}/batches/${batchId}`);
  }

  /**
   * Get check activities
   * GET /api/v1/checks/{checkId}/activities
   * @param {string} checkId - Check ID (UUID)
   * @param {number} page - Page number (default: 0)
   * @param {number} size - Page size (default: 20)
   */
  async getCheckActivities(checkId, page = 0, size = 20) {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('size', size);
    
    return this.get(`/api/v1/checks/${checkId}/activities?${queryParams.toString()}`);
  }

  // ==================== DRL Management APIs ====================

  /**
   * Search DRL dashboard with filters (POST)
   * POST /api/v1/drl/dashboard/search
   * @param {Object} searchParams - Search parameters in request body
   * @param {Array<string>} searchParams.statuses - Status array (NOT_STARTED, IN_PROGRESS, COMPLETED, etc.)
   * @param {Array<string>} searchParams.practiceCodes - Practice codes array
   * @param {Array<string>} searchParams.locationCodes - Location codes array
   * @param {Array<string>} searchParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} searchParams.reporteeIds - Reporter user IDs array (UUIDs)
   * @param {string} searchParams.drlNumber - DRL number (supports wildcards)
   * @param {string} searchParams.drlReceivedFrom - DRL received date from (YYYY-MM-DD)
   * @param {string} searchParams.drlReceivedTo - DRL received date to (YYYY-MM-DD)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 50)
   */
  async searchDrlDashboard(searchParams = {}) {
    const endpoint = '/api/v1/drl/dashboard/search';
    
    // Build request body, only including non-empty values
    const body = {};
    
    // Arrays - only include if not empty
    if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
      body.statuses = searchParams.statuses;
    }
    if (searchParams.excludeStatuses && Array.isArray(searchParams.excludeStatuses) && searchParams.excludeStatuses.length > 0) {
      body.excludeStatuses = searchParams.excludeStatuses;
    }
    if (searchParams.practiceCodes && Array.isArray(searchParams.practiceCodes) && searchParams.practiceCodes.length > 0) {
      body.practiceCodes = searchParams.practiceCodes;
    }
    if (searchParams.excludePracticeCodes && Array.isArray(searchParams.excludePracticeCodes) && searchParams.excludePracticeCodes.length > 0) {
      body.excludePracticeCodes = searchParams.excludePracticeCodes;
    }
    if (searchParams.locationCodes && Array.isArray(searchParams.locationCodes) && searchParams.locationCodes.length > 0) {
      body.locationCodes = searchParams.locationCodes;
    }
    if (searchParams.excludeLocationCodes && Array.isArray(searchParams.excludeLocationCodes) && searchParams.excludeLocationCodes.length > 0) {
      body.excludeLocationCodes = searchParams.excludeLocationCodes;
    }
    if (searchParams.assigneeIds && Array.isArray(searchParams.assigneeIds) && searchParams.assigneeIds.length > 0) {
      body.assigneeIds = searchParams.assigneeIds;
    }
    if (searchParams.excludeAssigneeIds && Array.isArray(searchParams.excludeAssigneeIds) && searchParams.excludeAssigneeIds.length > 0) {
      body.excludeAssigneeIds = searchParams.excludeAssigneeIds;
    }
    if (searchParams.reporteeIds && Array.isArray(searchParams.reporteeIds) && searchParams.reporteeIds.length > 0) {
      body.reporteeIds = searchParams.reporteeIds;
    }
    if (searchParams.excludeReporteeIds && Array.isArray(searchParams.excludeReporteeIds) && searchParams.excludeReporteeIds.length > 0) {
      body.excludeReporteeIds = searchParams.excludeReporteeIds;
    }
    if (searchParams.assigneeTypes && Array.isArray(searchParams.assigneeTypes) && searchParams.assigneeTypes.length > 0) {
      body.assigneeTypes = searchParams.assigneeTypes;
    }
    if (searchParams.reporteeTypes && Array.isArray(searchParams.reporteeTypes) && searchParams.reporteeTypes.length > 0) {
      body.reporteeTypes = searchParams.reporteeTypes;
    }
    if (searchParams.reconciliationStatuses && Array.isArray(searchParams.reconciliationStatuses) && searchParams.reconciliationStatuses.length > 0) {
      body.reconciliationStatuses = searchParams.reconciliationStatuses;
    }
    
    // Strings - only include if not empty
    if (searchParams.drlNumber && searchParams.drlNumber.trim() !== '') {
      body.drlNumber = searchParams.drlNumber.trim();
    }
    if (searchParams.batchNumber && searchParams.batchNumber.trim() !== '') {
      body.batchNumber = searchParams.batchNumber.trim();
    }
    
    // Dates - only include if not empty
    if (searchParams.drlReceivedFrom && searchParams.drlReceivedFrom.trim() !== '') {
      body.drlReceivedFrom = searchParams.drlReceivedFrom.trim();
    }
    if (searchParams.drlReceivedTo && searchParams.drlReceivedTo.trim() !== '') {
      body.drlReceivedTo = searchParams.drlReceivedTo.trim();
    }
    if (searchParams.createdAtFrom && searchParams.createdAtFrom.trim() !== '') {
      body.createdAtFrom = searchParams.createdAtFrom.trim();
    }
    if (searchParams.createdAtTo && searchParams.createdAtTo.trim() !== '') {
      body.createdAtTo = searchParams.createdAtTo.trim();
    }
    if (searchParams.completedDateFrom && searchParams.completedDateFrom.trim() !== '') {
      body.completedDateFrom = searchParams.completedDateFrom.trim();
    }
    if (searchParams.completedDateTo && searchParams.completedDateTo.trim() !== '') {
      body.completedDateTo = searchParams.completedDateTo.trim();
    }
    if (searchParams.reconciliationDateFrom && searchParams.reconciliationDateFrom.trim() !== '') {
      body.reconciliationDateFrom = searchParams.reconciliationDateFrom.trim();
    }
    if (searchParams.reconciliationDateTo && searchParams.reconciliationDateTo.trim() !== '') {
      body.reconciliationDateTo = searchParams.reconciliationDateTo.trim();
    }
    
    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 50;
    
    return this.post(endpoint, body);
  }

  /**
   * Get DRL by ID
   * GET /api/v1/drl/{drlId}
   * @param {string} drlId - DRL ID (UUID)
   */
  async getDrlById(drlId) {
    return this.get(`/api/v1/drl/${drlId}`);
  }

  /**
   * Delete DRL
   * DELETE /api/v1/drl/{drlId}
   * @param {string} drlId - DRL ID (UUID)
   */
  async deleteDrl(drlId) {
    return this.delete(`/api/v1/drl/${drlId}`);
  }

  /**
   * Create a new DRL
   * POST /api/v1/drl
   * @param {Object} drlData - DRL data
   */
  async createDrl(drlData) {
    return this.post('/api/v1/drl', drlData);
  }

  /**
   * Update DRL
   * PATCH /api/v1/drl/{drlId}
   * @param {string} drlId - DRL ID (UUID)
   * @param {Object} drlData - DRL data to update
   */
  async updateDrl(drlId, drlData) {
    return this.patch(`/api/v1/drl/${drlId}`, drlData);
  }

  /**
   * Create batch for a DRL
   * POST /api/v1/drl/{drlId}/batches
   * @param {string} drlId - DRL ID (UUID)
   * @param {Object} batchData - Batch data
   */
  async createDrlBatch(drlId, batchData) {
    return this.post(`/api/v1/drl/${drlId}/batches`, batchData);
  }

  /**
   * Update DRL batch
   * PATCH /api/v1/drl/{drlId}/batches/{batchId}
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {Object} batchData - Batch data to update
   */
  async updateDrlBatch(drlId, batchId, batchData) {
    return this.patch(`/api/v1/drl/${drlId}/batches/${batchId}`, batchData);
  }

  /**
   * Archive/Unarchive DRL batch
   * POST /api/v1/drl/{drlId}/batches/{batchId}/archive?archived={true|false}
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {boolean} archived - Whether to archive (true) or unarchive (false)
   */
  async archiveDrlBatch(drlId, batchId, archived = true) {
    return this.post(`/api/v1/drl/${drlId}/batches/${batchId}/archive?archived=${archived}`, {});
  }

  /**
   * Delete DRL batch
   * DELETE /api/v1/drl/{drlId}/batches/{batchId}
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   */
  async deleteDrlBatch(drlId, batchId) {
    return this.delete(`/api/v1/drl/${drlId}/batches/${batchId}`);
  }

  /**
   * Create a new clarification for a DRL
   * POST /api/v1/drl/{drlId}/clarifications
   * @param {string} drlId - DRL ID (UUID)
   * @param {Object} clarificationData - Clarification data
   */
  async createDrlClarification(drlId, clarificationData) {
    return this.post(`/api/v1/drl/${drlId}/clarifications`, clarificationData);
  }

  /**
   * Update a DRL clarification
   * PATCH /api/v1/drl/{drlId}/clarifications/{clarificationId}
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} clarificationId - Clarification ID (UUID)
   * @param {Object} clarificationData - Clarification data to update (can include newComment)
   */
  async updateDrlClarification(drlId, clarificationId, clarificationData) {
    return this.patch(`/api/v1/drl/${drlId}/clarifications/${clarificationId}`, clarificationData);
  }

  /**
   * Add a comment to a DRL clarification (and optionally update assignee/reportee)
   * PATCH /api/v1/drl/{drlId}/clarifications/{clarificationId}/comment
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} clarificationId - Clarification ID (UUID)
   * @param {Object} commentData - Comment data
   * @param {string} [commentData.newComment] - New comment text
   * @param {string} [commentData.assignee] - Assignee type (ON-SHORE/OFF-SHORE/EBOTICS)
   * @param {string} [commentData.reportee] - Reportee type (ON-SHORE/OFF-SHORE/EBOTICS)
   */
  async addDrlClarificationComment(drlId, clarificationId, commentData) {
    return this.patch(`/api/v1/drl/${drlId}/clarifications/${clarificationId}/comment`, commentData);
  }

  /**
   * Search DRL clarifications dashboard with filters (POST)
   * POST /api/v1/drl/clarifications/dashboard/search
   * @param {Object} searchParams - Search parameters in request body
   * @param {Array<string>} searchParams.statuses - Clarification statuses array (OPEN, RESOLVED)
   * @param {Array<string>} searchParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} searchParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {string} searchParams.drlNumber - DRL number (supports wildcards)
   * @param {string} searchParams.drlReceivedDateFrom - DRL received date from (YYYY-MM-DD)
   * @param {string} searchParams.drlReceivedDateTo - DRL received date to (YYYY-MM-DD)
   * @param {string} searchParams.resolvedDateFrom - Resolved date from (YYYY-MM-DD)
   * @param {string} searchParams.resolvedDateTo - Resolved date to (YYYY-MM-DD)
   * @param {string} searchParams.openedDateFrom - Opened date from (YYYY-MM-DD)
   * @param {string} searchParams.openedDateTo - Opened date to (YYYY-MM-DD)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 50)
   */
  async searchDrlClarificationsDashboard(searchParams = {}) {
    const endpoint = '/api/v1/drl/clarifications/dashboard/search';
    
    // Build request body, only including non-empty values
    const body = {};
    
    // Arrays - only include if not empty
    if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
      body.statuses = searchParams.statuses;
    }
    if (searchParams.excludeStatuses && Array.isArray(searchParams.excludeStatuses) && searchParams.excludeStatuses.length > 0) {
      body.excludeStatuses = searchParams.excludeStatuses;
    }
    if (searchParams.practiceCodes && Array.isArray(searchParams.practiceCodes) && searchParams.practiceCodes.length > 0) {
      body.practiceCodes = searchParams.practiceCodes;
    }
    if (searchParams.excludePracticeCodes && Array.isArray(searchParams.excludePracticeCodes) && searchParams.excludePracticeCodes.length > 0) {
      body.excludePracticeCodes = searchParams.excludePracticeCodes;
    }
    if (searchParams.locationCodes && Array.isArray(searchParams.locationCodes) && searchParams.locationCodes.length > 0) {
      body.locationCodes = searchParams.locationCodes;
    }
    if (searchParams.excludeLocationCodes && Array.isArray(searchParams.excludeLocationCodes) && searchParams.excludeLocationCodes.length > 0) {
      body.excludeLocationCodes = searchParams.excludeLocationCodes;
    }
    if (searchParams.assigneeIds && Array.isArray(searchParams.assigneeIds) && searchParams.assigneeIds.length > 0) {
      body.assigneeIds = searchParams.assigneeIds;
    }
    if (searchParams.excludeAssigneeIds && Array.isArray(searchParams.excludeAssigneeIds) && searchParams.excludeAssigneeIds.length > 0) {
      body.excludeAssigneeIds = searchParams.excludeAssigneeIds;
    }
    if (searchParams.reporterIds && Array.isArray(searchParams.reporterIds) && searchParams.reporterIds.length > 0) {
      body.reporterIds = searchParams.reporterIds;
    }
    if (searchParams.excludeReporterIds && Array.isArray(searchParams.excludeReporterIds) && searchParams.excludeReporterIds.length > 0) {
      body.excludeReporterIds = searchParams.excludeReporterIds;
    }
    if (searchParams.assigneeTypes && Array.isArray(searchParams.assigneeTypes) && searchParams.assigneeTypes.length > 0) {
      body.assigneeTypes = searchParams.assigneeTypes;
    }
    if (searchParams.reporteeTypes && Array.isArray(searchParams.reporteeTypes) && searchParams.reporteeTypes.length > 0) {
      body.reporteeTypes = searchParams.reporteeTypes;
    }
    if (searchParams.reconciliationStatuses && Array.isArray(searchParams.reconciliationStatuses) && searchParams.reconciliationStatuses.length > 0) {
      body.reconciliationStatuses = searchParams.reconciliationStatuses;
    }
    
    // Strings - only include if not empty
    if (searchParams.drlNumber && searchParams.drlNumber.trim() !== '') {
      body.drlNumber = searchParams.drlNumber.trim();
    }
    if (searchParams.clarificationType && searchParams.clarificationType.trim() !== '') {
      body.clarificationType = searchParams.clarificationType.trim();
    }
    
    // Boolean - only include if defined
    if (searchParams.unknown !== undefined && searchParams.unknown !== null) {
      body.unknown = searchParams.unknown;
    }
    
    // Dates - only include if not empty
    if (searchParams.drlReceivedDateFrom && searchParams.drlReceivedDateFrom.trim() !== '') {
      body.drlReceivedDateFrom = searchParams.drlReceivedDateFrom.trim();
    }
    if (searchParams.drlReceivedDateTo && searchParams.drlReceivedDateTo.trim() !== '') {
      body.drlReceivedDateTo = searchParams.drlReceivedDateTo.trim();
    }
    if (searchParams.resolvedDateFrom && searchParams.resolvedDateFrom.trim() !== '') {
      body.resolvedDateFrom = searchParams.resolvedDateFrom.trim();
    }
    if (searchParams.resolvedDateTo && searchParams.resolvedDateTo.trim() !== '') {
      body.resolvedDateTo = searchParams.resolvedDateTo.trim();
    }
    if (searchParams.openedDateFrom && searchParams.openedDateFrom.trim() !== '') {
      body.openedDateFrom = searchParams.openedDateFrom.trim();
    }
    if (searchParams.openedDateTo && searchParams.openedDateTo.trim() !== '') {
      body.openedDateTo = searchParams.openedDateTo.trim();
    }
    
    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 50;
    
    return this.post(endpoint, body);
  }

  /**
   * Get DRL activities
   * GET /api/v1/drl/{drlId}/activities
   * @param {string} drlId - DRL ID (UUID)
   * @param {number} page - Page number (default: 0)
   * @param {number} size - Page size (default: 20)
   */
  async getDrlActivities(drlId, page = 0, size = 20) {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('size', size);
    
    return this.get(`/api/v1/drl/${drlId}/activities?${queryParams.toString()}`);
  }

  /**
   * Allocate a check batch to DRL batch
   * POST /api/v1/drl/{drlId}/batches/{drlBatchId}/check-batches
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} drlBatchId - DRL Batch ID (UUID)
   * @param {Object} allocationData - Allocation data
   * @param {string} allocationData.checkId - Check ID (UUID)
   * @param {string} allocationData.checkBatchId - Check Batch ID (UUID)
   * @param {number} allocationData.amount - Allocation amount
   */
  async allocateCheckBatchToDrlBatch(drlId, drlBatchId, allocationData) {
    return this.post(`/api/v1/drl/${drlId}/batches/${drlBatchId}/check-batches`, allocationData);
  }

  /**
   * Deallocate a check batch from DRL batch
   * DELETE /api/v1/drl/{drlId}/batches/{drlBatchId}/check-batches/{checkBatchId}
   * @param {string} drlId - DRL ID (UUID)
   * @param {string} drlBatchId - DRL Batch ID (UUID)
   * @param {string} checkBatchId - Check Batch ID (UUID)
   */
  async deallocateCheckBatchFromDrlBatch(drlId, drlBatchId, checkBatchId) {
    return this.delete(`/api/v1/drl/${drlId}/batches/${drlBatchId}/check-batches/${checkBatchId}`);
  }

  /**
   * Search check batches by filters
   * POST /api/v1/drl/check-batches/search
   * @param {Object} searchParams - Search parameters in request body
   * @param {string} searchParams.practiceCode - Practice code
   * @param {string} searchParams.locationCode - Location code
   * @param {string} searchParams.batchNumber - Batch number (optional; use with or without depositDate)
   * @param {string} searchParams.depositDate - Deposit date (YYYY-MM-DD) (optional; use with or without batchNumber)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 200)
   */
  async searchCheckBatches(searchParams = {}) {
    const endpoint = '/api/v1/drl/check-batches/search';

    // Build request body, only including non-empty values
    const body = {};

    if (searchParams.practiceCode && searchParams.practiceCode.trim() !== '') {
      body.practiceCode = searchParams.practiceCode.trim();
    }
    if (searchParams.locationCode && searchParams.locationCode.trim() !== '') {
      body.locationCode = searchParams.locationCode.trim();
    }
    if (searchParams.batchNumber && searchParams.batchNumber.trim() !== '') {
      body.batchNumber = searchParams.batchNumber.trim();
    }
    if (searchParams.depositDate && searchParams.depositDate.trim() !== '') {
      body.depositDate = searchParams.depositDate.trim();
    }

    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 200;

    return this.post(endpoint, body);
  }

  // ==================== DIT/DRL Management APIs (Legacy) ====================

  /**
   * Search DIT/DRL dashboard with filters (POST)
   * POST /api/v1/dit-drl/dashboard/search
   * @param {Object} searchParams - Search parameters in request body
   * @param {Array<string>} searchParams.statuses - Status array (NOT_STARTED, IN_PROGRESS, COMPLETED, etc.)
   * @param {Array<string>} searchParams.siteCodes - Site codes array
   * @param {Array<string>} searchParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} searchParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {string} searchParams.siteCode - Site code (supports wildcards like "*ANN")
   * @param {string} searchParams.batchNumber - Batch number (supports wildcards like "B-00*")
   * @param {string} searchParams.dateReceivedFrom - Date received from (YYYY-MM-DD)
   * @param {string} searchParams.dateReceivedTo - Date received to (YYYY-MM-DD)
   * @param {string} searchParams.completedDateFrom - Completed date from (YYYY-MM-DD)
   * @param {string} searchParams.completedDateTo - Completed date to (YYYY-MM-DD)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 50)
   */
  async searchDitDrlDashboard(searchParams = {}) {
    const endpoint = '/api/v1/dit-drl/dashboard/search';
    
    // Build request body, only including non-empty values
    const body = {};
    
    // Arrays - only include if not empty
    if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
      body.statuses = searchParams.statuses;
    }
    if (searchParams.siteCodes && Array.isArray(searchParams.siteCodes) && searchParams.siteCodes.length > 0) {
      body.siteCodes = searchParams.siteCodes;
    }
    if (searchParams.assigneeIds && Array.isArray(searchParams.assigneeIds) && searchParams.assigneeIds.length > 0) {
      body.assigneeIds = searchParams.assigneeIds;
    }
    if (searchParams.excludeAssigneeIds && Array.isArray(searchParams.excludeAssigneeIds) && searchParams.excludeAssigneeIds.length > 0) {
      body.excludeAssigneeIds = searchParams.excludeAssigneeIds;
    }
    if (searchParams.reporterIds && Array.isArray(searchParams.reporterIds) && searchParams.reporterIds.length > 0) {
      body.reporterIds = searchParams.reporterIds;
    }
    if (searchParams.excludeReporterIds && Array.isArray(searchParams.excludeReporterIds) && searchParams.excludeReporterIds.length > 0) {
      body.excludeReporterIds = searchParams.excludeReporterIds;
    }
    
    // Strings - only include if not empty
    if (searchParams.siteCode && searchParams.siteCode.trim() !== '') {
      body.siteCode = searchParams.siteCode.trim();
    }
    if (searchParams.batchNumber && searchParams.batchNumber.trim() !== '') {
      body.batchNumber = searchParams.batchNumber.trim();
    }
    
    // Dates - only include if not empty
    if (searchParams.dateReceivedFrom && searchParams.dateReceivedFrom.trim() !== '') {
      body.dateReceivedFrom = searchParams.dateReceivedFrom.trim();
    }
    if (searchParams.dateReceivedTo && searchParams.dateReceivedTo.trim() !== '') {
      body.dateReceivedTo = searchParams.dateReceivedTo.trim();
    }
    if (searchParams.completedDateFrom && searchParams.completedDateFrom.trim() !== '') {
      body.completedDateFrom = searchParams.completedDateFrom.trim();
    }
    if (searchParams.completedDateTo && searchParams.completedDateTo.trim() !== '') {
      body.completedDateTo = searchParams.completedDateTo.trim();
    }
    
    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 50;
    
    return this.post(endpoint, body);
  }

  /**
   * Get DIT/DRL by ID
   * GET /api/v1/dit-drl/{ditDrlId}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   */
  async getDitDrlById(ditDrlId) {
    return this.get(`/api/v1/dit-drl/${ditDrlId}`);
  }

  /**
   * Create a new DIT/DRL
   * POST /api/v1/dit-drl
   * @param {Object} ditDrlData - DIT/DRL data
   */
  async createDitDrl(ditDrlData) {
    return this.post('/api/v1/dit-drl', ditDrlData);
  }

  /**
   * Update DIT/DRL
   * PATCH /api/v1/dit-drl/{ditDrlId}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {Object} ditDrlData - DIT/DRL data to update
   */
  async updateDitDrl(ditDrlId, ditDrlData) {
    return this.patch(`/api/v1/dit-drl/${ditDrlId}`, ditDrlData);
  }

  /**
   * Create batch for a DIT/DRL
   * POST /api/v1/dit-drl/{ditDrlId}/batches
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {Object} batchData - Batch data
   */
  async createDitDrlBatch(ditDrlId, batchData) {
    return this.post(`/api/v1/dit-drl/${ditDrlId}/batches`, batchData);
  }

  /**
   * Update DIT/DRL batch
   * PATCH /api/v1/dit-drl/{ditDrlId}/batches/{batchId}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {Object} batchData - Batch data to update
   */
  async updateDitDrlBatch(ditDrlId, batchId, batchData) {
    return this.patch(`/api/v1/dit-drl/${ditDrlId}/batches/${batchId}`, batchData);
  }

  /**
   * Archive/Unarchive DIT/DRL batch
   * POST /api/v1/dit-drl/{ditDrlId}/batches/{batchId}/archive?archived={true|false}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   * @param {boolean} archived - Whether to archive (true) or unarchive (false)
   */
  async archiveDitDrlBatch(ditDrlId, batchId, archived = true) {
    return this.post(`/api/v1/dit-drl/${ditDrlId}/batches/${batchId}/archive?archived=${archived}`, {});
  }

  /**
   * Delete DIT/DRL batch
   * DELETE /api/v1/dit-drl/{ditDrlId}/batches/{batchId}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {string} batchId - Batch ID (UUID)
   */
  async deleteDitDrlBatch(ditDrlId, batchId) {
    return this.delete(`/api/v1/dit-drl/${ditDrlId}/batches/${batchId}`);
  }

  /**
   * Get all clarifications for a DIT/DRL
   * GET /api/v1/dit-drl/{ditDrlId}/clarifications
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   */
  async getDitDrlClarifications(ditDrlId) {
    return this.get(`/api/v1/dit-drl/${ditDrlId}/clarifications`);
  }

  /**
   * Create a new clarification for a DIT/DRL
   * POST /api/v1/dit-drl/{ditDrlId}/clarifications
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {Object} clarificationData - Clarification data
   * @param {string} clarificationData.clarificationType - Type of clarification
   * @param {string} clarificationData.details - Clarification details
   * @param {string} clarificationData.assigneeId - Assignee user ID (UUID)
   * @param {string} clarificationData.reporterId - Reporter user ID (UUID)
   * @param {string} [clarificationData.assignee] - Assignee type (ON-SHORE/OFF-SHORE)
   * @param {string} [clarificationData.reportee] - Reportee type (EBOTICS)
   * @param {string} [clarificationData.status] - Clarification status (OPEN/RESOLVED)
   */
  async createDitDrlClarification(ditDrlId, clarificationData) {
    return this.post(`/api/v1/dit-drl/${ditDrlId}/clarifications`, clarificationData);
  }

  /**
   * Update a DIT/DRL clarification
   * PATCH /api/v1/dit-drl/{ditDrlId}/clarifications/{clarificationId}
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {string} clarificationId - Clarification ID (UUID)
   * @param {Object} clarificationData - Clarification data to update (can include newComment)
   * @param {string} [clarificationData.clarificationType] - Type of clarification
   * @param {string} [clarificationData.details] - Clarification details
   * @param {string} [clarificationData.assigneeId] - Assignee user ID (UUID)
   * @param {string} [clarificationData.reporterId] - Reporter user ID (UUID)
   * @param {string} [clarificationData.assignee] - Assignee type (ON-SHORE/OFF-SHORE)
   * @param {string} [clarificationData.reportee] - Reportee type (EBOTICS)
   * @param {string} [clarificationData.status] - Clarification status (OPEN/RESOLVED)
   * @param {string} [clarificationData.newComment] - New comment to add
   */
  async updateDitDrlClarification(ditDrlId, clarificationId, clarificationData) {
    return this.patch(`/api/v1/dit-drl/${ditDrlId}/clarifications/${clarificationId}`, clarificationData);
  }

  /**
   * Get DIT/DRL activities
   * GET /api/v1/dit-drl/{ditDrlId}/activities
   * @param {string} ditDrlId - DIT/DRL ID (UUID)
   * @param {number} page - Page number (default: 0)
   * @param {number} size - Page size (default: 20)
   */
  async getDitDrlActivities(ditDrlId, page = 0, size = 20) {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('size', size);
    
    return this.get(`/api/v1/dit-drl/${ditDrlId}/activities?${queryParams.toString()}`);
  }

  // ==================== Clarification Management APIs ====================

  /**
   * Get clarifications dashboard with filters
   * GET /api/v1/clarifications/dashboard
   * @param {Object} params - Query parameters
   * @param {string} params.status - Clarification status
   * @param {string} params.assigneeId - Assignee user ID (UUID)
   * @param {string} params.reporterId - Reporter user ID (UUID)
   * @param {string} params.checkNumber - Check number
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {number} params.month - Month (1-12)
   * @param {number} params.year - Year
   * @param {number} params.page - Page number (default: 0)
   * @param {number} params.size - Page size (default: 50)
   */
  async getClarificationsDashboard(params = {}) {
    const queryParams = new URLSearchParams();
    
    // Add all non-empty parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/clarifications/dashboard${queryString ? `?${queryString}` : ''}`;
    
    return this.get(endpoint);
  }

  /**
   * Search clarifications dashboard with filters (POST)
   * POST /api/v1/clarifications/dashboard/search
   * @param {Object} searchParams - Search parameters in request body
   * @param {Array<string>} searchParams.statuses - Clarification statuses array (OPEN, RESOLVED)
   * @param {Array<string>} searchParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} searchParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {Array<string>} searchParams.checkTypes - Check types array (EFT, ERA, DIT_DRL, etc.)
   * @param {Array<string>} searchParams.workflowStatuses - Workflow status codes array
   * @param {string} searchParams.checkNumber - Check number (supports wildcards like "*CHE")
   * @param {string} searchParams.depositDateFrom - Deposit date from (YYYY-MM-DD)
   * @param {string} searchParams.depositDateTo - Deposit date to (YYYY-MM-DD)
   * @param {string} searchParams.resolvedDateFrom - Resolved date from (YYYY-MM-DD)
   * @param {string} searchParams.resolvedDateTo - Resolved date to (YYYY-MM-DD)
   * @param {string} searchParams.openedDateFrom - Opened date from (YYYY-MM-DD)
   * @param {string} searchParams.openedDateTo - Opened date to (YYYY-MM-DD)
   * @param {number} searchParams.page - Page number (default: 0)
   * @param {number} searchParams.size - Page size (default: 50)
   */
  async searchClarificationsDashboard(searchParams = {}) {
    const endpoint = '/api/v1/clarifications/dashboard/search';
    
    // Build request body, only including non-empty values
    const body = {};
    
    // Arrays - only include if not empty
    if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
      body.statuses = searchParams.statuses;
    }
    if (searchParams.excludeStatuses && Array.isArray(searchParams.excludeStatuses) && searchParams.excludeStatuses.length > 0) {
      body.excludeStatuses = searchParams.excludeStatuses;
    }
    if (searchParams.practiceCodes && Array.isArray(searchParams.practiceCodes) && searchParams.practiceCodes.length > 0) {
      body.practiceCodes = searchParams.practiceCodes;
    }
    if (searchParams.excludePracticeCodes && Array.isArray(searchParams.excludePracticeCodes) && searchParams.excludePracticeCodes.length > 0) {
      body.excludePracticeCodes = searchParams.excludePracticeCodes;
    }
    if (searchParams.locationCodes && Array.isArray(searchParams.locationCodes) && searchParams.locationCodes.length > 0) {
      body.locationCodes = searchParams.locationCodes;
    }
    if (searchParams.excludeLocationCodes && Array.isArray(searchParams.excludeLocationCodes) && searchParams.excludeLocationCodes.length > 0) {
      body.excludeLocationCodes = searchParams.excludeLocationCodes;
    }
    if (searchParams.assigneeIds && Array.isArray(searchParams.assigneeIds) && searchParams.assigneeIds.length > 0) {
      body.assigneeIds = searchParams.assigneeIds;
    }
    if (searchParams.reporterIds && Array.isArray(searchParams.reporterIds) && searchParams.reporterIds.length > 0) {
      body.reporterIds = searchParams.reporterIds;
    }
    if (searchParams.assigneeTypes && Array.isArray(searchParams.assigneeTypes) && searchParams.assigneeTypes.length > 0) {
      body.assigneeTypes = searchParams.assigneeTypes;
    }
    if (searchParams.reporteeTypes && Array.isArray(searchParams.reporteeTypes) && searchParams.reporteeTypes.length > 0) {
      body.reporteeTypes = searchParams.reporteeTypes;
    }
    if (searchParams.checkTypes && Array.isArray(searchParams.checkTypes) && searchParams.checkTypes.length > 0) {
      body.checkTypes = searchParams.checkTypes;
    }
    if (searchParams.excludeCheckTypes && Array.isArray(searchParams.excludeCheckTypes) && searchParams.excludeCheckTypes.length > 0) {
      body.excludeCheckTypes = searchParams.excludeCheckTypes;
    }
    if (searchParams.workflowStatuses && Array.isArray(searchParams.workflowStatuses) && searchParams.workflowStatuses.length > 0) {
      body.workflowStatuses = searchParams.workflowStatuses;
    }
    if (searchParams.excludeWorkflowStatuses && Array.isArray(searchParams.excludeWorkflowStatuses) && searchParams.excludeWorkflowStatuses.length > 0) {
      body.excludeWorkflowStatuses = searchParams.excludeWorkflowStatuses;
    }
    
    // Strings - only include if not empty
    if (searchParams.checkNumber && searchParams.checkNumber.trim() !== '') {
      body.checkNumber = searchParams.checkNumber.trim();
    }
    if (searchParams.clarificationType && searchParams.clarificationType.trim() !== '') {
      body.clarificationType = searchParams.clarificationType.trim();
    }
    if (searchParams.excludeClarificationTypes && Array.isArray(searchParams.excludeClarificationTypes) && searchParams.excludeClarificationTypes.length > 0) {
      body.excludeClarificationTypes = searchParams.excludeClarificationTypes;
    }
    
    // Boolean - only include if defined
    if (searchParams.unknown !== undefined && searchParams.unknown !== null) {
      body.unknown = searchParams.unknown;
    }
    
    // Dates - only include if not empty
    if (searchParams.depositDateFrom && searchParams.depositDateFrom.trim() !== '') {
      body.depositDateFrom = searchParams.depositDateFrom.trim();
    }
    if (searchParams.depositDateTo && searchParams.depositDateTo.trim() !== '') {
      body.depositDateTo = searchParams.depositDateTo.trim();
    }
    if (searchParams.resolvedDateFrom && searchParams.resolvedDateFrom.trim() !== '') {
      body.resolvedDateFrom = searchParams.resolvedDateFrom.trim();
    }
    if (searchParams.resolvedDateTo && searchParams.resolvedDateTo.trim() !== '') {
      body.resolvedDateTo = searchParams.resolvedDateTo.trim();
    }
    if (searchParams.openedDateFrom && searchParams.openedDateFrom.trim() !== '') {
      body.openedDateFrom = searchParams.openedDateFrom.trim();
    }
    if (searchParams.openedDateTo && searchParams.openedDateTo.trim() !== '') {
      body.openedDateTo = searchParams.openedDateTo.trim();
    }
    
    // Pagination - always include
    body.page = searchParams.page !== undefined ? searchParams.page : 0;
    body.size = searchParams.size !== undefined ? searchParams.size : 50;
    
    return this.post(endpoint, body);
  }

  /**
   * Get all clarifications for a check
   * GET /api/v1/checks/{checkId}/clarifications
   * @param {string} checkId - Check ID (UUID)
   */
  async getClarifications(checkId) {
    return this.get(`/api/v1/checks/${checkId}/clarifications`);
  }

  /**
   * Create a new clarification
   * POST /api/v1/checks/{checkId}/clarifications
   * @param {string} checkId - Check ID (UUID)
   * @param {Object} clarificationData - Clarification data
   */
  async createClarification(checkId, clarificationData) {
    return this.post(`/api/v1/checks/${checkId}/clarifications`, clarificationData);
  }

  /**
   * Update a clarification
   * PATCH /api/v1/checks/{checkId}/clarifications/{clarificationId}
   * @param {string} checkId - Check ID (UUID)
   * @param {string} clarificationId - Clarification ID (UUID)
   * @param {Object} clarificationData - Clarification data to update (can include newComment)
   */
  async updateClarification(checkId, clarificationId, clarificationData) {
    return this.patch(`/api/v1/checks/${checkId}/clarifications/${clarificationId}`, clarificationData);
  }

  /**
   * Add a comment to a clarification (and optionally update assignee/reportee)
   * PATCH /api/v1/checks/{checkId}/clarifications/{clarificationId}/comment
   * @param {string} checkId - Check ID (UUID)
   * @param {string} clarificationId - Clarification ID (UUID)
   * @param {Object} commentData - Comment data
   * @param {string} [commentData.newComment] - New comment text
   * @param {string} [commentData.assignee] - Assignee type (ON-SHORE/OFF-SHORE/EBOTICS)
   * @param {string} [commentData.reportee] - Reportee type (ON-SHORE/OFF-SHORE/EBOTICS)
   */
  async addClarificationComment(checkId, clarificationId, commentData) {
    return this.patch(`/api/v1/checks/${checkId}/clarifications/${clarificationId}/comment`, commentData);
  }

  // ==================== Bulk Import APIs ====================

  /**
   * Upload CSV/XLSX file for bulk import
   * POST /api/v1/bulk-import
   * @param {File} file - CSV or XLSX file
   * @param {string} [jobName] - Optional job name
   * @param {string} [assigneeId] - Optional assignee ID (UUID)
   */
  async uploadBulkImportFile(file, jobName = null, assigneeId = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    let queryParams = [];
    if (jobName) {
      queryParams.push(`jobName=${encodeURIComponent(jobName)}`);
    }
    if (assigneeId) {
      queryParams.push(`assigneeId=${encodeURIComponent(assigneeId)}`);
    }
    
    const endpoint = `/api/v1/bulk-import${queryParams.length > 0 ? `?${queryParams.join('&')}` : ''}`;
    
    return this.uploadWithProgress(endpoint, formData, options);
  }

  /**
   * Search bulk import dashboard jobs with filters
   * POST /api/v1/bulk-import/dashboard/jobs
   */
  async getBulkImportJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/bulk-import/dashboard/jobs', body);
  }

  /**
   * Get bulk import dashboard (legacy)
   * GET /api/v1/bulk-import/dashboard
   */
  async getBulkImportDashboard() {
    return this.get('/api/v1/bulk-import/dashboard');
  }

  /**
   * Get bulk import job by ID
   * GET /api/v1/bulk-import/{jobId}
   * @param {string} jobId - Job ID (UUID)
   */
  async getBulkImportJob(jobId) {
    return this.get(`/api/v1/bulk-import/${jobId}`);
  }

  /**
   * Update bulk import job (assignee/reporter defaults)
   * PUT /api/v1/bulk-import/{jobId}
   * @param {string} jobId - Job ID (UUID)
   * @param {Object} updateData - Update data
   * @param {string} [updateData.assigneeId] - Assignee ID (UUID)
   * @param {string} [updateData.reporterId] - Reporter ID (UUID)
   */
  async updateBulkImportJob(jobId, updateData) {
    return this.put(`/api/v1/bulk-import/${jobId}`, updateData);
  }

  /**
   * Get staged checks for a bulk import job
   * GET /api/v1/bulk-import/{jobId}/checks
   * @param {string} jobId - Job ID (UUID)
   * @param {number} [page=0] - Page number
   * @param {number} [size=50] - Page size
   */
  async getBulkImportJobChecks(jobId, page = 0, size = 50) {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('size', size);
    
    return this.get(`/api/v1/bulk-import/${jobId}/checks?${queryParams.toString()}`);
  }

  /**
   * Re-validate bulk import job
   * POST /api/v1/bulk-import/{jobId}/re-validate
   * @param {string} jobId - Job ID (UUID)
   */
  async revalidateBulkImportJob(jobId) {
    return this.post(`/api/v1/bulk-import/${jobId}/re-validate`, {});
  }

  /**
   * Promote valid staged rows from bulk import job
   * POST /api/v1/bulk-import/{jobId}/promote
   * @param {string} jobId - Job ID (UUID)
   * @param {boolean} [allowPartials=false] - Allow partial promotion
   */
  async promoteBulkImportJob(jobId, allowPartials = false) {
    const queryParams = new URLSearchParams();
    queryParams.append('allowPartials', allowPartials);
    
    return this.post(`/api/v1/bulk-import/${jobId}/promote?${queryParams.toString()}`, {});
  }

  /**
   * Update a single staged check
   * PATCH /api/v1/bulk-import/{jobId}/checks/{stagingCheckId}
   * @param {string} jobId - Job ID (UUID)
   * @param {string} stagingCheckId - Staging check ID (UUID)
   * @param {Object} checkData - Check data to update
   */
  async updateStagedCheck(jobId, stagingCheckId, checkData) {
    return this.patch(`/api/v1/bulk-import/${jobId}/checks/${stagingCheckId}`, checkData);
  }

  /**
   * Bulk update staged checks, then re-validate
   * PUT /api/v1/bulk-import/{jobId}/checks
   * @param {string} jobId - Job ID (UUID)
   * @param {Array<Object>} items - Array of check items to update
   * @param {string} items[].stagingCheckId - Staging check ID (UUID)
   * @param {Object} items[].checkData - Check data fields to update
   */
  async bulkUpdateStagedChecks(jobId, items) {
    return this.put(`/api/v1/bulk-import/${jobId}/checks`, { items });
  }

  /**
   * Delete bulk import job and all staged rows
   * DELETE /api/v1/bulk-import/{jobId}
   * @param {string} jobId - Job ID (UUID)
   */
  async deleteBulkImportJob(jobId) {
    return this.delete(`/api/v1/bulk-import/${jobId}`);
  }

  // ==================== External Batch Validation APIs ====================

  /**
   * Search batch validation jobs with filters
   * POST /api/v1/external-batch-validation/dashboard/jobs
   * @param {Object} params - Search parameters
   * @param {string} [params.jobName] - Job name filter
   * @param {string} [params.practiceCode] - Practice code filter
   * @param {string} [params.locationCode] - Location code filter
   * @param {string[]} [params.practiceCodes] - Practice codes array
   * @param {string[]} [params.locationCodes] - Location codes array
   * @param {string[]} [params.createdByUserIds] - Created by user IDs
   * @param {string[]} [params.updatedByUserIds] - Updated by user IDs
   * @param {string} [params.fileName] - File name filter
   * @param {string} [params.createdDateFrom] - Created date from (YYYY-MM-DD)
   * @param {string} [params.createdDateTo] - Created date to (YYYY-MM-DD)
   * @param {number} [params.page] - Page number (default: 0)
   * @param {number} [params.size] - Page size (default: 200)
   */
  async getBatchValidationJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.practiceCode) body.practiceCode = params.practiceCode;
    if (params.locationCode) body.locationCode = params.locationCode;
    if (params.practiceCodes?.length) body.practiceCodes = params.practiceCodes;
    if (params.locationCodes?.length) body.locationCodes = params.locationCodes;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/external-batch-validation/dashboard/jobs', body);
  }

  /**
   * Upload CSV/XLSX file for batch validation
   * POST /api/v1/external-batch-validation/job?practiceCode=...&locationCode=...&jobName=...
   * @param {File} file - CSV or XLSX file
   * @param {string} practiceCode - Practice code (required)
   * @param {string} locationCode - Location code (required)
   * @param {string} [jobName] - Optional job name
   */
  async createBatchValidationJob(file, practiceCode, locationCode, jobName = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);

    const queryParams = [`practiceCode=${encodeURIComponent(practiceCode)}`, `locationCode=${encodeURIComponent(locationCode)}`];
    if (jobName) queryParams.push(`jobName=${encodeURIComponent(jobName)}`);

    return this.uploadWithProgress(`/api/v1/external-batch-validation/job?${queryParams.join('&')}`, formData, options);
  }

  /**
   * Get batch validation job by ID
   * GET /api/v1/external-batch-validation/job/{jobId}
   * @param {string} jobId - Job ID (UUID)
   */
  async getBatchValidationJob(jobId) {
    return this.get(`/api/v1/external-batch-validation/job/${jobId}`);
  }

  /**
   * Delete batch validation job
   * DELETE /api/v1/external-batch-validation/job/{jobId}
   * @param {string} jobId - Job ID (UUID)
   */
  async deleteBatchValidationJob(jobId) {
    return this.delete(`/api/v1/external-batch-validation/job/${jobId}`);
  }

  /**
   * Get batches for a batch validation job (with filters)
   * POST /api/v1/external-batch-validation/job/{jobId}/batches
   * @param {string} jobId - Job ID (UUID)
   * @param {Object} params - Filter/pagination params
   * @param {number} [params.page] - Page number
   * @param {number} [params.size] - Page size
   * @param {string[]} [params.rowStatuses] - Filter by row statuses
   * @param {string[]} [params.errorSearches] - Filter by error searches
   * @param {string} [params.errorSearch] - Error search string
   */
  async getBatchValidationJobBatches(jobId, params = {}) {
    const body = {};
    body.page = params.page ?? 0;
    body.size = params.size ?? 50;
    if (params.rowStatuses?.length) body.rowStatuses = params.rowStatuses;
    if (params.errorSearches?.length) body.errorSearches = params.errorSearches;
    if (params.errorSearch) body.errorSearch = params.errorSearch;
    return this.post(`/api/v1/external-batch-validation/job/${jobId}/batches`, body);
  }

  /**
   * Update a staged batch (full fields)
   * PATCH /api/v1/external-batch-validation/job/{jobId}/batches/{stagingBatchId}
   * @param {string} jobId - Job ID (UUID)
   * @param {string} stagingBatchId - Staging batch ID (UUID)
   * @param {Object} data - Update data
   */
  async updateBatchValidationStagingBatch(jobId, stagingBatchId, data) {
    return this.patch(`/api/v1/external-batch-validation/job/${jobId}/batches/${stagingBatchId}`, data);
  }

  /**
   * Delete a single staged batch row
   * DELETE /api/v1/external-batch-validation/job/{jobId}/batches/{stagingBatchId}
   * @param {string} jobId - Job ID (UUID)
   * @param {string} stagingBatchId - Staging batch ID (UUID)
   */
  async deleteBatchValidationStagingBatch(jobId, stagingBatchId) {
    return this.delete(`/api/v1/external-batch-validation/job/${jobId}/batches/${stagingBatchId}`);
  }

  /**
   * Promote a staged batch row into check batch
   * POST /api/v1/external-batch-validation/job/{jobId}/batches/{stagingBatchId}/promote
   * @param {string} jobId - Job ID (UUID)
   * @param {string} stagingBatchId - Staging batch ID (UUID)
   * @param {Object} data - Promote payload
   */
  async promoteBatchValidationStagingBatch(jobId, stagingBatchId, data) {
    return this.post(`/api/v1/external-batch-validation/job/${jobId}/batches/${stagingBatchId}/promote`, data);
  }

  /**
   * Re-validate a batch validation job
   * POST /api/v1/external-batch-validation/job/{jobId}/revalidate
   * @param {string} jobId - Job ID (UUID)
   */
  async revalidateBatchValidationJob(jobId) {
    return this.post(`/api/v1/external-batch-validation/job/${jobId}/revalidate`, {});
  }

  /**
   * Export batch validation report as Excel file
   * POST /api/v1/external-batch-validation-report/job/{jobId}/export
   * @param {string} jobId - Job ID (UUID)
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportBatchValidationReport(jobId) {
    const url = `${this.baseURL}/api/v1/external-batch-validation-report/job/${jobId}/export`;
    const headers = this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return response.blob();
  }

  // ==================== Bulk Batch Upload APIs ====================

  /**
   * Search bulk batch upload jobs with filters
   * POST /api/v1/bulk-batch-upload/dashboard/jobs
   */
  async getBulkBatchUploadJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.practiceCode) body.practiceCode = params.practiceCode;
    if (params.locationCode) body.locationCode = params.locationCode;
    if (params.practiceCodes?.length) body.practiceCodes = params.practiceCodes;
    if (params.locationCodes?.length) body.locationCodes = params.locationCodes;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/bulk-batch-upload/dashboard/jobs', body);
  }

  /**
   * Upload CSV/XLSX file for bulk batch upload
   * POST /api/v1/bulk-batch-upload/job?practiceCode=...&locationCode=...&jobName=...
   */
  async createBulkBatchUploadJob(file, practiceCode, locationCode, jobName = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);

    const queryParams = [`practiceCode=${encodeURIComponent(practiceCode)}`, `locationCode=${encodeURIComponent(locationCode)}`];
    if (jobName) queryParams.push(`jobName=${encodeURIComponent(jobName)}`);

    return this.uploadWithProgress(`/api/v1/bulk-batch-upload/job?${queryParams.join('&')}`, formData, options);
  }

  /**
   * Get bulk batch upload job by ID
   * GET /api/v1/bulk-batch-upload/job/{jobId}
   */
  async getBulkBatchUploadJob(jobId) {
    return this.get(`/api/v1/bulk-batch-upload/job/${jobId}`);
  }

  /**
   * Delete bulk batch upload job
   * DELETE /api/v1/bulk-batch-upload/job/{jobId}
   */
  async deleteBulkBatchUploadJob(jobId) {
    return this.delete(`/api/v1/bulk-batch-upload/job/${jobId}`);
  }

  /**
   * Promote all valid rows in a bulk batch upload job
   * POST /api/v1/bulk-batch-upload/job/{jobId}/promote
   */
  async promoteBulkBatchUploadJob(jobId) {
    return this.post(`/api/v1/bulk-batch-upload/job/${jobId}/promote`, {});
  }

  /**
   * Revalidate a bulk batch upload job
   * POST /api/v1/bulk-batch-upload/job/{jobId}/revalidate
   */
  async revalidateBulkBatchUploadJob(jobId) {
    return this.post(`/api/v1/bulk-batch-upload/job/${jobId}/revalidate`, {});
  }

  /**
   * Search staged rows for a bulk batch upload job
   * POST /api/v1/bulk-batch-upload/job/{jobId}/rows
   */
  async getBulkBatchUploadJobRows(jobId, params = {}) {
    const body = {};
    body.page = params.page ?? 0;
    body.size = params.size ?? 50;
    if (params.rowStatuses?.length) body.rowStatuses = params.rowStatuses;
    if (params.errorSearch) body.errorSearch = params.errorSearch;
    if (params.errorSearches?.length) body.errorSearches = params.errorSearches;
    return this.post(`/api/v1/bulk-batch-upload/job/${jobId}/rows`, body);
  }

  /**
   * Update a staged row in a bulk batch upload job
   * PATCH /api/v1/bulk-batch-upload/job/{jobId}/rows/{stagingBatchId}
   */
  async updateBulkBatchUploadRow(jobId, stagingBatchId, data) {
    return this.patch(`/api/v1/bulk-batch-upload/job/${jobId}/rows/${stagingBatchId}`, data);
  }

  /**
   * Promote a single staged row in a bulk batch upload job
   * POST /api/v1/bulk-batch-upload/job/{jobId}/rows/{stagingBatchId}/promote
   */
  async promoteBulkBatchUploadRow(jobId, stagingBatchId) {
    return this.post(`/api/v1/bulk-batch-upload/job/${jobId}/rows/${stagingBatchId}/promote`, {});
  }

  /**
   * Export bulk batch upload error report as Excel file
   * POST /api/v1/bulk-batch-upload-report/job/{jobId}/export
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportBulkBatchUploadReport(jobId) {
    const url = `${this.baseURL}/api/v1/bulk-batch-upload-report/job/${jobId}/export`;
    const headers = this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return response.blob();
  }

  // ==================== Bulk Check Update APIs ====================

  /**
   * Search bulk check update jobs with filters
   * POST /api/v1/bulk-check-update/dashboard/jobs
   */
  async getBulkCheckUpdateJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.practiceCode) body.practiceCode = params.practiceCode;
    if (params.locationCode) body.locationCode = params.locationCode;
    if (params.practiceCodes?.length) body.practiceCodes = params.practiceCodes;
    if (params.locationCodes?.length) body.locationCodes = params.locationCodes;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/bulk-check-update/dashboard/jobs', body);
  }

  /**
   * Create bulk check update job (upload file)
   * POST /api/v1/bulk-check-update/job
   */
  async createBulkCheckUpdateJob(file, practiceCode, locationCode, jobName = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);

    const queryParams = [`practiceCode=${encodeURIComponent(practiceCode)}`, `locationCode=${encodeURIComponent(locationCode)}`];
    if (jobName) queryParams.push(`jobName=${encodeURIComponent(jobName)}`);

    return this.uploadWithProgress(`/api/v1/bulk-check-update/job?${queryParams.join('&')}`, formData, options);
  }

  /**
   * Get bulk check update job by ID
   * GET /api/v1/bulk-check-update/job/{jobId}
   */
  async getBulkCheckUpdateJob(jobId) {
    return this.get(`/api/v1/bulk-check-update/job/${jobId}`);
  }

  /**
   * Delete bulk check update job
   * DELETE /api/v1/bulk-check-update/job/{jobId}
   */
  async deleteBulkCheckUpdateJob(jobId) {
    return this.delete(`/api/v1/bulk-check-update/job/${jobId}`);
  }

  /**
   * Promote all valid rows in a bulk check update job
   * POST /api/v1/bulk-check-update/job/{jobId}/promote
   */
  async promoteBulkCheckUpdateJob(jobId) {
    return this.post(`/api/v1/bulk-check-update/job/${jobId}/promote`, {});
  }

  /**
   * Revalidate a bulk check update job
   * POST /api/v1/bulk-check-update/job/{jobId}/revalidate
   */
  async revalidateBulkCheckUpdateJob(jobId) {
    return this.post(`/api/v1/bulk-check-update/job/${jobId}/revalidate`, {});
  }

  /**
   * Search staged rows for a bulk check update job
   * POST /api/v1/bulk-check-update/job/{jobId}/rows
   */
  async getBulkCheckUpdateJobRows(jobId, params = {}) {
    const body = {};
    body.page = params.page ?? 0;
    body.size = params.size ?? 50;
    if (params.rowStatuses?.length) body.rowStatuses = params.rowStatuses;
    if (params.errorSearch) body.errorSearch = params.errorSearch;
    if (params.errorSearches?.length) body.errorSearches = params.errorSearches;
    return this.post(`/api/v1/bulk-check-update/job/${jobId}/rows`, body);
  }

  /**
   * Update a staged row in a bulk check update job
   * PATCH /api/v1/bulk-check-update/job/{jobId}/rows/{stagingUpdateId}
   */
  async updateBulkCheckUpdateRow(jobId, stagingUpdateId, data) {
    return this.patch(`/api/v1/bulk-check-update/job/${jobId}/rows/${stagingUpdateId}`, data);
  }

  /**
   * Promote a single staged row in a bulk check update job
   * POST /api/v1/bulk-check-update/job/{jobId}/rows/{stagingUpdateId}/promote
   */
  async promoteBulkCheckUpdateRow(jobId, stagingUpdateId) {
    return this.post(`/api/v1/bulk-check-update/job/${jobId}/rows/${stagingUpdateId}/promote`, {});
  }

  /**
   * Delete a staged row in a bulk check update job
   * DELETE /api/v1/bulk-check-update/job/{jobId}/rows/{stagingUpdateId}
   */
  async deleteBulkCheckUpdateRow(jobId, stagingUpdateId) {
    return this.delete(`/api/v1/bulk-check-update/job/${jobId}/rows/${stagingUpdateId}`);
  }

  /**
   * Export bulk check update error report as Excel file
   * POST /api/v1/bulk-check-update-report/job/{jobId}/export
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportBulkCheckUpdateReport(jobId) {
    const url = `${this.baseURL}/api/v1/bulk-check-update-report/job/${jobId}/export`;
    const headers = this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return response.blob();
  }

  // ==================== Recon Validation APIs ====================

  /**
   * Recon validation dashboard jobs (paginated)
   * POST /api/v1/recon-validation/dashboard/jobs
   */
  async getReconValidationJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.practiceCode) body.practiceCode = params.practiceCode;
    if (params.locationCode) body.locationCode = params.locationCode;
    if (params.practiceCodes?.length) body.practiceCodes = params.practiceCodes;
    if (params.locationCodes?.length) body.locationCodes = params.locationCodes;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/recon-validation/dashboard/jobs', body);
  }

  /**
   * Create recon validation job (upload XLSX)
   * POST /api/v1/recon-validation/job?practiceCode=...&locationCode=...&jobName=...
   */
  async createReconValidationJob(file, practiceCode, locationCode, jobName = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = [`practiceCode=${encodeURIComponent(practiceCode)}`, `locationCode=${encodeURIComponent(locationCode)}`];
    if (jobName) queryParams.push(`jobName=${encodeURIComponent(jobName)}`);
    return this.uploadWithProgress(`/api/v1/recon-validation/job?${queryParams.join('&')}`, formData, options);
  }

  /**
   * GET /api/v1/recon-validation/job/{jobId}
   */
  async getReconValidationJob(jobId) {
    return this.get(`/api/v1/recon-validation/job/${jobId}`);
  }

  /**
   * DELETE /api/v1/recon-validation/job/{jobId}
   */
  async deleteReconValidationJob(jobId) {
    return this.delete(`/api/v1/recon-validation/job/${jobId}`);
  }

  /**
   * POST /api/v1/recon-validation/job/{jobId}/revalidate
   */
  async revalidateReconValidationJob(jobId) {
    return this.post(`/api/v1/recon-validation/job/${jobId}/revalidate`, {});
  }

  /**
   * Search staged rows for a recon validation job
   * POST /api/v1/recon-validation/job/{jobId}/rows
   */
  async getReconValidationJobRows(jobId, params = {}) {
    const body = {};
    body.page = params.page ?? 0;
    body.size = params.size ?? 50;
    if (params.rowStatuses?.length) body.rowStatuses = params.rowStatuses;
    if (params.errorSearch) body.errorSearch = params.errorSearch;
    if (params.errorSearches?.length) body.errorSearches = params.errorSearches;
    return this.post(`/api/v1/recon-validation/job/${jobId}/rows`, body);
  }

  /**
   * PATCH /api/v1/recon-validation/job/{jobId}/rows/{stagingRowId}
   */
  async updateReconValidationRow(jobId, stagingRowId, data) {
    return this.patch(`/api/v1/recon-validation/job/${jobId}/rows/${stagingRowId}`, data);
  }

  /**
   * DELETE /api/v1/recon-validation/job/{jobId}/rows/{stagingRowId}
   */
  async deleteReconValidationRow(jobId, stagingRowId) {
    return this.delete(`/api/v1/recon-validation/job/${jobId}/rows/${stagingRowId}`);
  }

  /**
   * Export recon validation report (XLSX)
   * POST /api/v1/recon-validation/job/{jobId}/export
   */
  async exportReconValidationReport(jobId) {
    const url = `${this.baseURL}/api/v1/recon-validation/job/${jobId}/export`;
    const headers = this.getHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });
    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return response.blob();
  }

  /**
   * Export recon validation error codes + rules catalog (XLSX)
   * POST /api/v1/recon-validation-report/errors/export
   * Requires x-user-id (via getHeaders)
   */
  async exportReconValidationErrorsCatalog() {
    const url = `${this.baseURL}/api/v1/recon-validation-report/errors/export`;
    const headers = this.getHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });
    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return response.blob();
  }

  // ==================== Clarification Validation APIs ====================

  /**
   * Clarification validation dashboard jobs (paginated)
   * POST /api/v1/clarification-validation/dashboard/jobs
   */
  async getClarificationValidationJobs(params = {}) {
    const body = {};
    if (params.jobName) body.jobName = params.jobName;
    if (params.createdByUserIds?.length) body.createdByUserIds = params.createdByUserIds;
    if (params.updatedByUserIds?.length) body.updatedByUserIds = params.updatedByUserIds;
    if (params.fileName) body.fileName = params.fileName;
    if (params.createdDateFrom) body.createdDateFrom = params.createdDateFrom;
    if (params.createdDateTo) body.createdDateTo = params.createdDateTo;
    body.page = params.page ?? 0;
    body.size = params.size ?? 200;
    return this.post('/api/v1/clarification-validation/dashboard/jobs', body);
  }

  /**
   * Create clarification validation job (upload XLSX)
   * POST /api/v1/clarification-validation/job?practiceCode=...&locationCode=...&jobName=...
   */
  async createClarificationValidationJob(file, practiceCode, locationCode, jobName = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    const queryParams = [`practiceCode=${encodeURIComponent(practiceCode)}`, `locationCode=${encodeURIComponent(locationCode)}`];
    if (jobName) queryParams.push(`jobName=${encodeURIComponent(jobName)}`);
    return this.uploadWithProgress(`/api/v1/clarification-validation/job?${queryParams.join('&')}`, formData, options);
  }

  /**
   * GET /api/v1/clarification-validation/job/{jobId}
   */
  async getClarificationValidationJob(jobId) {
    return this.get(`/api/v1/clarification-validation/job/${jobId}`);
  }

  /**
   * DELETE /api/v1/clarification-validation/job/{jobId}
   */
  async deleteClarificationValidationJob(jobId) {
    return this.delete(`/api/v1/clarification-validation/job/${jobId}`);
  }

  /**
   * POST /api/v1/clarification-validation/job/{jobId}/revalidate
   */
  async revalidateClarificationValidationJob(jobId) {
    return this.post(`/api/v1/clarification-validation/job/${jobId}/revalidate`, {});
  }

  /**
   * Search staged rows for a clarification validation job
   * POST /api/v1/clarification-validation/job/{jobId}/rows
   */
  async getClarificationValidationJobRows(jobId, params = {}) {
    const body = {};
    body.page = params.page ?? 0;
    body.size = params.size ?? 50;
    if (params.rowStatuses?.length) body.rowStatuses = params.rowStatuses;
    if (params.errorSearch) body.errorSearch = params.errorSearch;
    if (params.errorSearches?.length) body.errorSearches = params.errorSearches;
    return this.post(`/api/v1/clarification-validation/job/${jobId}/rows`, body);
  }

  /**
   * PATCH /api/v1/clarification-validation/job/{jobId}/rows/{stagingRowId}
   */
  async updateClarificationValidationRow(jobId, stagingRowId, data) {
    return this.patch(`/api/v1/clarification-validation/job/${jobId}/rows/${stagingRowId}`, data);
  }

  /**
   * DELETE /api/v1/clarification-validation/job/{jobId}/rows/{stagingRowId}
   */
  async deleteClarificationValidationRow(jobId, stagingRowId) {
    return this.delete(`/api/v1/clarification-validation/job/${jobId}/rows/${stagingRowId}`);
  }

  /**
   * Export clarification validation report (XLSX)
   * POST /api/v1/clarification-validation-report/job/{jobId}/export
   */
  async exportClarificationValidationReport(jobId) {
    const url = `${this.baseURL}/api/v1/clarification-validation-report/job/${jobId}/export`;
    const headers = this.getHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });
    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      const error = new Error('Unauthorized. Please login again.');
      error.status = response.status;
      throw error;
    }
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const data = isJson ? await response.json() : await response.text();
      const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return response.blob();
  }

  /**
   * Get dashboard KPIs (status percentage + amounts summary)
   * POST /api/v1/checks/analytics/kpis
   * @param {Object} filters - Filter parameters (optional)
   * @param {string} filters.depositDateFrom - Deposit date from (YYYY-MM-DD)
   * @param {string} filters.depositDateTo - Deposit date to (YYYY-MM-DD)
   * @param {string} filters.receivedDateFrom - Received date from (YYYY-MM-DD)
   * @param {string} filters.receivedDateTo - Received date to (YYYY-MM-DD)
   * @param {string} filters.completedDateFrom - Completed date from (YYYY-MM-DD)
   * @param {string} filters.completedDateTo - Completed date to (YYYY-MM-DD)
   * @param {Array<string>} filters.practiceCodes - Practice codes array
   * @param {boolean} filters.unknown - Whether to include unknown checks
   * @returns {Promise<Object>} KPI response with status breakdown and amount summary
   */
  async getDashboardKPIs(filters = {}) {
    return this.post('/api/v1/checks/analytics/kpis', filters);
  }

  // ==================== DRL Analytics APIs ====================

  /**
   * Get DRL dashboard KPIs (status percentage + amounts summary)
   * POST /api/v1/drl/analytics/kpis
   * @param {Object} filters - Filter parameters (optional)
   * @param {Array<string>} filters.practiceCodes - Practice codes array
   * @param {Array<string>} filters.locationCodes - Location codes array
   * @param {Array<string>} filters.statuses - Status array
   * @param {Array<string>} filters.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} filters.reporteeIds - Reporter user IDs array (UUIDs)
   * @param {string} filters.drlNumber - DRL number (supports wildcards)
   * @param {string} filters.drlReceivedFrom - DRL received date from (YYYY-MM-DD)
   * @param {string} filters.drlReceivedTo - DRL received date to (YYYY-MM-DD)
   * @param {string} filters.createdDateFrom - Created date from (YYYY-MM-DD)
   * @param {string} filters.createdDateTo - Created date to (YYYY-MM-DD)
   * @returns {Promise<Object>} KPI response with status breakdown and amount summary
   */
  async getDrlDashboardKPIs(filters = {}) {
    return this.post('/api/v1/drl/analytics/kpis', filters);
  }

  /**
   * Get DRL dashboard KPIs comparison (multiple segments)
   * POST /api/v1/drl/analytics/kpis/compare
   * @param {Object} payload - Comparison payload
   * @param {Object} payload.shared - Shared filters across all segments
   * @param {Array<Object>} payload.segments - Array of segment filters with key and filters
   * @returns {Promise<Object>} Comparison response with KPI data for each segment
   */
  async getDrlDashboardKPIsCompare(payload = {}) {
    return this.post('/api/v1/drl/analytics/kpis/compare', payload);
  }

  // ==================== Check Report Export API ====================

  /**
   * Export check report as Excel file
   * POST /api/v1/check-report/export
   * @param {Object} exportParams - Export parameters (filters)
   * @param {Array<string>} exportParams.statuses - Check statuses array
   * @param {Array<string>} exportParams.practiceCodes - Practice codes array
   * @param {Array<string>} exportParams.locationCodes - Location codes array
   * @param {Array<string>} exportParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} exportParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {Array<string>} exportParams.checkTypes - Check types array
   * @param {Array<string>} exportParams.workflowStatuses - Workflow status codes array
   * @param {string} exportParams.checkType - Check type
   * @param {string} exportParams.checkNumber - Check number
   * @param {string} exportParams.payer - Payer name
   * @param {boolean} exportParams.unknown - Unknown flag
   * @param {string} exportParams.depositDateFrom - Deposit date from (YYYY-MM-DD)
   * @param {string} exportParams.depositDateTo - Deposit date to (YYYY-MM-DD)
   * @param {string} exportParams.receivedDateFrom - Received date from (YYYY-MM-DD)
   * @param {string} exportParams.receivedDateTo - Received date to (YYYY-MM-DD)
   * @param {string} exportParams.completedDateFrom - Completed date from (YYYY-MM-DD)
   * @param {string} exportParams.completedDateTo - Completed date to (YYYY-MM-DD)
   * @param {number} exportParams.page - Page number
   * @param {number} exportParams.size - Page size
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportCheckReport(exportParams = {}) {
    const url = `${this.baseURL}/api/v1/check-report/export`;
    
    const headers = this.getHeaders();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(exportParams),
      });
      
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        this.logout();
        window.location.href = '/login';
        const error = new Error('Unauthorized. Please login again.');
        error.status = response.status;
        throw error;
      }
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const data = isJson ? await response.json() : await response.text();
        const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }
      
      // Return blob for Excel file download
      return await response.blob();
    } catch (error) {
      console.error('Export Check Report Error:', error);
      throw error;
    }
  }

  /**
   * Export DRL report as Excel file
   * POST /api/v1/drl-report/export
   * @param {Object} exportParams - Export parameters (filters)
   * @param {Array<string>} exportParams.practiceCodes - Practice codes array
   * @param {Array<string>} exportParams.locationCodes - Location codes array
   * @param {Array<string>} exportParams.statuses - Status array
   * @param {Array<string>} exportParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} exportParams.reporteeIds - Reporter user IDs array (UUIDs)
   * @param {string} exportParams.drlNumber - DRL number
   * @param {string} exportParams.drlReceivedFrom - DRL received date from (YYYY-MM-DD)
   * @param {string} exportParams.drlReceivedTo - DRL received date to (YYYY-MM-DD)
   * @param {string} exportParams.reconciliationDateFrom - Reconciliation date from (YYYY-MM-DD)
   * @param {string} exportParams.reconciliationDateTo - Reconciliation date to (YYYY-MM-DD)
   * @param {string} exportParams.completedDateFrom - Completed date from (YYYY-MM-DD)
   * @param {string} exportParams.completedDateTo - Completed date to (YYYY-MM-DD)
   * @param {string} exportParams.createdAtFrom - Created at from (YYYY-MM-DD)
   * @param {string} exportParams.createdAtTo - Created at to (YYYY-MM-DD)
   * @param {number} exportParams.page - Page number
   * @param {number} exportParams.size - Page size
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportDrlReport(exportParams = {}) {
    const url = `${this.baseURL}/api/v1/drl-report/export`;
    
    const headers = this.getHeaders();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(exportParams),
      });
      
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        this.logout();
        window.location.href = '/login';
        const error = new Error('Unauthorized. Please login again.');
        error.status = response.status;
        throw error;
      }
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const data = isJson ? await response.json() : await response.text();
        const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }
      
      // Return blob for Excel file download
      return await response.blob();
    } catch (error) {
      console.error('Export DRL Report Error:', error);
      throw error;
    }
  }

  /**
   * Export Clarification report as Excel file
   * POST /api/v1/clarification-report/export
   * @param {Object} exportParams - Export parameters (filters)
   * @param {Array<string>} exportParams.statuses - Status array
   * @param {Array<string>} exportParams.assigneeIds - Assignee user IDs array (UUIDs)
   * @param {Array<string>} exportParams.reporterIds - Reporter user IDs array (UUIDs)
   * @param {Array<string>} exportParams.assignees - Assignee names array
   * @param {Array<string>} exportParams.assigneeTypes - Assignee types array (ON-SHORE, OFF-SHORE)
   * @param {Array<string>} exportParams.reportees - Reporter names array
   * @param {Array<string>} exportParams.reporteeTypes - Reporter types array (ON-SHORE, OFF-SHORE)
   * @param {string} exportParams.checkNumber - Check number
   * @param {string} exportParams.clarificationType - Clarification type
   * @param {Array<string>} exportParams.practiceCodes - Practice codes array
   * @param {Array<string>} exportParams.locationCodes - Location codes array
   * @param {Array<string>} exportParams.checkTypes - Check types array
   * @param {Array<string>} exportParams.workflowStatuses - Workflow status codes array
   * @param {boolean} exportParams.unknown - Unknown flag
   * @param {string} exportParams.depositDateFrom - Deposit date from (YYYY-MM-DD)
   * @param {string} exportParams.depositDateTo - Deposit date to (YYYY-MM-DD)
   * @param {Array<string>} exportParams.checkAssigneeIds - Check assignee IDs array (UUIDs)
   * @param {Array<string>} exportParams.checkReporterIds - Check reporter IDs array (UUIDs)
   * @param {string} exportParams.openedDateFrom - Opened date from (YYYY-MM-DD)
   * @param {string} exportParams.openedDateTo - Opened date to (YYYY-MM-DD)
   * @param {string} exportParams.resolvedDateFrom - Resolved date from (YYYY-MM-DD)
   * @param {string} exportParams.resolvedDateTo - Resolved date to (YYYY-MM-DD)
   * @param {number} exportParams.page - Page number
   * @param {number} exportParams.size - Page size
   * @returns {Promise<Blob>} Excel file blob
   */
  // ==================== Saved Filters APIs ====================

  /**
   * List saved filters for the current user filtered by page type.
   * GET /api/v1/me/saved-filters?type={type}
   * @param {'CHECK_DASHBOARD'|'CHECK_KPI_DASHBOARD'|'CLARIFICATION_DASHBOARD'} type
   * @returns {Promise<Array>} Array of saved filter objects
   */
  async getSavedFilters(type) {
    return this.get(`/api/v1/me/saved-filters?type=${type}`);
  }

  /**
   * Create a new saved filter for the current user.
   * POST /api/v1/me/saved-filters
   * @param {Object} data - { type, name, description, payload, sortBy, sortDirection, displayOrder }
   * @returns {Promise<Object>} Created saved filter with id
   */
  async createSavedFilter(data) {
    return this.post('/api/v1/me/saved-filters', data);
  }

  /**
   * Update an existing saved filter.
   * PUT /api/v1/me/saved-filters/{id}
   * @param {string} id - UUID of the saved filter
   * @param {Object} data - { name, description, payload, sortBy, sortDirection, displayOrder }
   * @returns {Promise<Object>} Updated saved filter
   */
  async updateSavedFilter(id, data) {
    return this.put(`/api/v1/me/saved-filters/${id}`, data);
  }

  /**
   * Delete a saved filter by id.
   * DELETE /api/v1/me/saved-filters/{id}
   * @param {string} id - UUID of the saved filter
   * @returns {Promise<void>}
   */
  async deleteSavedFilter(id) {
    return this.delete(`/api/v1/me/saved-filters/${id}`);
  }

  // ==================== Clarification Report Export ====================

  async exportClarificationReport(exportParams = {}) {
    const url = `${this.baseURL}/api/v1/clarification-report/export`;
    
    const headers = this.getHeaders();
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(exportParams),
      });
      
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        this.logout();
        window.location.href = '/login';
        const error = new Error('Unauthorized. Please login again.');
        error.status = response.status;
        throw error;
      }
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const data = isJson ? await response.json() : await response.text();
        const errorMessage = data?.message || data?.error || data || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }
      
      // Return blob for Excel file download
      return await response.blob();
    } catch (error) {
      console.error('Export Clarification Report Error:', error);
      throw error;
    }
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

